/* ============================================================
   COLLECTION-CARD SWATCH INTERACTIONS (mobile-first, RTL, no-JS-safe).
   Ido / 2026-06-30 (workflow wf_239e3279 follow-up). Two behaviours, one
   delegated listener pair (click + keydown):
     1) Tap/click a colour swatch -> swap the card's MAIN <img> to that
        variant image IN PLACE (no PDP navigation).
     2) Tap/click the "+N" pill   -> reveal the overflow swatches inline.
   Works for the 'blank' grid block (swatches are <a> at the card root, with
   an href no-JS fallback) AND the 'horizontal' block (swatches are <span>
   INSIDE the card's <a class="grid-product__link"> — so we stopPropagation
   to keep that parent link from firing; the parent link IS the no-JS
   fallback there).
   Delegated from document so it survives the collection AJAX re-render and
   the recommendations / hotspots grids; gated by a window flag so it binds
   ONCE. The legacy theme.js hover/touch swap no longer binds to these cards
   (we dropped the .color-swatch--with-image class), so there is no conflict.
   Revertible: delete this file + its <script> in layout/theme.liquid + the
   data-swatch* hooks in snippets/product-grid-item.liquid.
   ============================================================ */
(function () {
  if (window.__cardSwatchInit) return;        // bind once
  window.__cardSwatchInit = true;

  /* One polite live region so AT users hear the colour change / reveal. */
  var live;
  function announce(msg) {
    if (!msg) return;
    if (!live) {
      live = document.createElement('div');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      live.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
      document.body.appendChild(live);
    }
    live.textContent = '';
    window.requestAnimationFrame(function () { live.textContent = msg; });
  }

  /* Find the card's MAIN product <img> from any node inside the card.
     tikhatikim uses a fixed (portrait) ratio -> the primary image lives in
     .grid__image-ratio; the hover/secondary image is NOT inside it, so this
     never grabs the wrong picture. Falls back for natural-ratio configs. */
  function findCardImage(fromEl) {
    var card = fromEl.closest('.grid-product') || fromEl.closest('.grid__item');
    if (!card) return null;
    var scope = card.querySelector('.grid-product__image-mask') || card;
    return scope.querySelector('.grid__image-ratio img') ||
           scope.querySelector('.image-wrap img') ||
           scope.querySelector('.grid-product__image');
  }

  /* Swap to a single explicit URL, beating native lazy + srcset. Decode the
     new image first (via onload) so we never replace a good photo with a
     broken one, and force the <img> visible (it carries .image-element which
     is opacity:0 until scrolled in). */
  function swapCardImage(img, url, alt) {
    if (!img || !url) return;
    var pre = new Image();
    pre.onload = function () {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = url;
      img.loading = 'eager';
      img.style.opacity = '1';               // override .image-element{opacity:0}
      if (alt) img.alt = alt;
      var host = img.closest('image-element');
      if (host) host.classList.add('aos-animate');
      var wrap = img.closest('.image-wrap');
      if (wrap) wrap.classList.add('loaded');
    };
    pre.onerror = function () { /* broken URL -> keep the current image */ };
    pre.src = url;
  }

  function activateSwatch(swatch, e) {
    e.preventDefault();
    e.stopPropagation();                       // blank: own href; horizontal: parent card link
    var group = swatch.closest('[data-swatch-group]');
    if (group) {
      group.querySelectorAll('[data-swatch]').forEach(function (s) {
        s.removeAttribute('aria-current');
      });
    }
    swatch.setAttribute('aria-current', 'true');
    var label = swatch.getAttribute('aria-label') || '';
    var url = swatch.getAttribute('data-variant-image');
    if (url) swapCardImage(findCardImage(swatch), url, label);   // no url -> keep image, ring still moves
    announce(label);
  }

  function activateMore(pill, e) {
    e.preventDefault();
    e.stopPropagation();
    var group = pill.closest('[data-swatch-group]');
    if (!group) return;
    group.classList.add('is-expanded');        // CSS un-hides .color-swatch--overflow
    var firstHidden = group.querySelector('.color-swatch--overflow');
    pill.style.display = 'none';               // drop the pill (CSS also hides it)
    if (firstHidden && firstHidden.focus) firstHidden.focus();   // don't black-hole keyboard focus
    announce(pill.getAttribute('aria-label') || '');
  }

  function dispatch(target, e) {
    var more = target.closest && target.closest('[data-swatch-more]');
    if (more) { activateMore(more, e); return; }
    var swatch = target.closest && target.closest('[data-swatch]');
    if (swatch) { activateSwatch(swatch, e); }
  }

  document.addEventListener('click', function (e) { dispatch(e.target, e); }, false);

  /* Keyboard: <a> already activates on Enter (native click) — don't double-fire.
     Space on <a> does nothing natively, and <span role=button> (horizontal)
     needs both Enter and Space — handle those here. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== ' ' && e.key !== 'Spacebar' && e.key !== 'Enter') return;
    var el = e.target.closest && (e.target.closest('[data-swatch-more]') || e.target.closest('[data-swatch]'));
    if (!el) return;
    if (e.key === 'Enter' && el.tagName === 'A') return;   // native handles it
    dispatch(e.target, e);
  }, false);
})();

/* ============================================================
   COLLECTION SCROLL RESTORE ON BACK-FROM-PDP (iOS Safari).
   Ido / 2026-07-01.

   BUG: On a collection, scroll down -> tap a product -> PDP -> browser
   Back jumps the collection to the grid top instead of restoring scroll.
   Impulse 9.1 AJAX-renders filters/sort/pagination and its popstate
   handler (theme.js bindBackButton ~6778) fires on EVERY Back — including
   Back-from-PDP — calling renderCollectionPage -> updateScroll(false)
   (theme.js 6765) -> window.scrollTo(gridTop). That async scroll-to-top
   lands after a network round-trip, so it wins the race and stomps
   whatever iOS Safari restored (bfcache or scrollRestoration:'auto').

   FIX (no theme.js edit): save scrollY keyed by the FULL collection URL
   when we actually leave the document to a PDP (pagehide + visibilitychange
   + link tap — NEVER beforeunload, which breaks bfcache). On return, arm a
   one-shot restore on 'pageshow', then re-assert the saved scroll AFTER the
   theme's updateScroll runs by hooking the theme's own 'collection:reloaded'
   event (theme.js 6769) plus a bounded rAF watchdog. So we win the race
   deterministically and restore exactly once.

   SCOPED to Back-from-PDP only — cannot affect filter/sort/pagination:
     * We only SAVE a key when the document actually unloads (leaving to a
       PDP). In-collection filter/sort/pagination are same-document
       pushState+AJAX; they never unload, so they never write a key.
     * The key is the full href incl. query, so a filtered/paginated/sorted
       view is a DIFFERENT key — no stale offset can leak across them.
     * We only ARM on 'pageshow' (a real document show). Filter/sort/
       pagination AJAX never fires pageshow.
   Two independent gates: a matching saved key must exist AND a pageshow
   must have fired. Both required to restore.

   Revertible: delete this IIFE from assets/custom.js. Nothing else changes.
   ============================================================ */
/* rev2 (2026-07-01, after adversarial review): sync re-assert on
   collection:reloaded (kills the top-then-back flicker); height-aware restore
   that waits (up to 3s HARD_CAP) for images to grow the page tall enough on a
   fresh Back-reload before giving up, so a deep scroll isn't dropped; prunes
   sibling keys on save (no sessionStorage accumulation); excludes swatch taps
   (which don't navigate) from saving. */
(function () {
  if (window.__collScrollRestoreInit) return;   // bind once
  window.__collScrollRestoreInit = true;

  /* "Collection page" detection: the theme's AJAX grid anchor. Server-rendered
     on load, so if absent now it stays absent for this document -> nothing to
     do. (Present on collection + search grids — both share the buggy path.) */
  if (!document.querySelector('[data-scroll-to]')) return;

  var KEY_PREFIX = 'tk_collScroll:';
  /* Key on the FULL href (path + query) so a filtered/sorted/paginated view is
     a DIFFERENT key than the plain collection we left from — the primary gate
     that scopes restore to Back-from-PDP. */
  function storageKey() { return KEY_PREFIX + window.location.href; }
  function currentY() { return window.pageYOffset || document.documentElement.scrollTop || 0; }
  function maxY() {
    var d = document.documentElement, b = document.body;
    return Math.max(b.scrollHeight, d.scrollHeight, d.clientHeight) - window.innerHeight;
  }

  // Keep only THIS url's key; drop every other collection key we wrote (no bloat).
  function pruneOthers() {
    try {
      var keep = storageKey(), i, k, kill = [];
      for (i = 0; i < sessionStorage.length; i++) {
        k = sessionStorage.key(i);
        if (k && k.indexOf(KEY_PREFIX) === 0 && k !== keep) kill.push(k);
      }
      kill.forEach(function (k) { sessionStorage.removeItem(k); });
    } catch (e) {}
  }

  function saveScroll() {
    try {
      var y = currentY();
      if (y > 0) { sessionStorage.setItem(storageKey(), String(y)); pruneOthers(); }
    } catch (e) { /* Safari private mode / quota -> degrade to native */ }
  }
  function readSaved() {
    try {
      var raw = sessionStorage.getItem(storageKey());
      if (raw == null) return null;
      var n = parseInt(raw, 10);
      return isNaN(n) ? null : n;
    } catch (e) { return null; }
  }
  function clearSaved() { try { sessionStorage.removeItem(storageKey()); } catch (e) {} }

  /* Own the scroll so the browser's auto-restore can't race the theme's async
     scroll-to-top. Does NOT disable bfcache — pageshow.persisted still fires. */
  if ('scrollRestoration' in history) {
    try { history.scrollRestoration = 'manual'; } catch (e) {}
  }

  /* ---------- SAVE: leaving the collection (to a PDP) ---------- */

  // pagehide fires reliably on iOS leaving the page and — unlike beforeunload —
  // does NOT evict bfcache.
  window.addEventListener('pagehide', saveScroll, { capture: true });
  // Backup for abrupt backgrounding where iOS skips the final pagehide.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') saveScroll();
  });
  // Freshest value: at the instant a product card is tapped. Capture-phase so
  // it survives the AJAX re-render. Skip swatch taps (they don't navigate, so a
  // saved key would never be consumed). (a.grid-product__link = product-grid-item.liquid:195)
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    if (e.target.closest('[data-swatch]') || e.target.closest('[data-swatch-more]')) return;
    if (e.target.closest('a.grid-product__link')) saveScroll();
  }, true);

  /* ---------- RESTORE: Back/Forward document show ---------- */

  var pending = 0;   // saved Y we still owe (0 == nothing armed)
  var deadline = 0;  // soft budget: only enforced once the page is tall enough
  var HARD_CAP = 0;  // absolute give-up even if the page never grows tall enough

  function applyRestore() {
    if (!pending) return;
    var target = pending;
    var reachable = maxY() >= target - 2;   // is the page physically tall enough yet?
    window.scrollTo(0, target);
    var now = currentY();
    if (Math.abs(now - target) <= 2) { pending = 0; clearSaved(); return; }   // landed
    if (reachable && Date.now() > deadline) { pending = 0; clearSaved(); return; } // tall enough, user moved on
    if (Date.now() > HARD_CAP) { pending = 0; clearSaved(); return; }          // never grew tall enough
    window.requestAnimationFrame(applyRestore);
  }

  function arm() {
    if (pending) return;               // already armed this show
    var y = readSaved();
    if (y == null) return;             // GATE #1: no saved key for THIS url
    pending = y;
    deadline = Date.now() + 600;       // don't fight the user forever once reachable
    HARD_CAP = Date.now() + 3000;      // ceiling for slow image loads on fresh Back
    window.requestAnimationFrame(applyRestore);
  }

  // The theme scrolls to grid top inside renderCollectionPage().then() and fires
  // this synchronously after (theme.js 6769). Re-assert in the SAME frame so the
  // grid-top scroll never paints (no flicker).
  document.addEventListener('collection:reloaded', function () {
    if (pending) applyRestore();
  });

  // GATE #2: restore ONLY on a genuine BACK/FORWARD navigation — not a fresh
  // visit (menu/link/typed URL) or a plain reload, which should start at top.
  // bfcache restore -> pageshow.persisted === true; fresh Back-reload ->
  // Navigation Timing type 'back_forward'. (In-collection filter/sort/pagination
  // are same-document AJAX and never fire pageshow at all.)
  function isBackForward(persisted) {
    if (persisted) return true;
    /* the in-stock default redirect (main-collection.liquid) reloads the page
       via location.replace, so a genuine Back lands here as type 'navigate' —
       the liquid stashes the back_forward fact in a one-shot flag instead */
    try {
      var bf = sessionStorage.getItem('tk_bfRedirect');
      if (bf != null) {
        sessionStorage.removeItem('tk_bfRedirect');
        if (bf === window.location.pathname) return true;
      }
    } catch (e) {}
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      if (nav && nav.type) return nav.type === 'back_forward';
    } catch (e) {}
    try { return performance.navigation && performance.navigation.type === 2; } catch (e) {}
    return false;
  }

  window.addEventListener('pageshow', function (e) { if (isBackForward(e.persisted)) arm(); });

  // Cold-load fallback if pageshow already fired before this script parsed.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { if (isBackForward(false)) arm(); });
  } else {
    if (isBackForward(false)) arm();
  }
})();

/* ============================================================
   PDP: hide the price while a SOLD-OUT variant is selected (Ido, 2026-07-01).
   Server-side product-template.liquid hides it on load (current_variant.available);
   this keeps it in sync when the shopper switches colour on the page. Impulse
   dispatches `variantChange` on the product section container ([data-section-type=
   "product"]) with detail.variant (has `.available`); a null variant = invalid combo.
   Scoped per-container so quick-view / recommendations can't cross-toggle. Revertible:
   delete this IIFE + revert the liquid `unless`.
   ============================================================ */
(function () {
  if (window.__pdpSoldOutPriceInit) return;
  window.__pdpSoldOutPriceInit = true;

  function apply(container, variant) {
    var price = container.querySelector('.product-block--price');
    if (!price) return;
    var soldOut = !variant || variant.available === false;
    price.classList.toggle('hide', soldOut);
  }

  function bind() {
    var containers = document.querySelectorAll('[data-section-type="product"]');
    for (var i = 0; i < containers.length; i++) {
      (function (c) {
        if (c.__soldOutPriceBound) return;
        c.__soldOutPriceBound = true;
        c.addEventListener('variantChange', function (e) {
          apply(c, e.detail && e.detail.variant);
        });
      })(containers[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();

/* ============================================================
   Translate + RTL the <shopify-account> login POPUP (Impulse 9.1, Ido 2026-07-01).
   Shopify's <shopify-account> web component renders its login/menu UI in ENGLISH even
   though the storefront locale is `he` (Shopify doesn't localise it here), and LTR in a
   corner. Its shadow root is OPEN, so we translate the text/aria/placeholder strings to
   Hebrew and force RTL. A MutationObserver re-applies on the component's async re-renders
   (the sign-in form loads lazily). Desktop-only (the icon is `small--hide`). Revertible:
   delete this IIFE + the ::part(dialog) rules in snippets/customer-account.liquid.
   ============================================================ */
(function () {
  if (window.__shopifyAccountHe) return;
  window.__shopifyAccountHe = true;

  var MAP = {
    "Sign in or create account": "התחברות / הרשמה",
    "Orders": "ההזמנות שלי",
    "Profile": "החשבון שלי",
    "Email": "אימייל",
    "Email me with news and offers": "עדכנו אותי במבצעים ובחדשות",
    "Account": "חשבון",
    "Close menu": "סגירה",
    "Quick links": "קישורים מהירים",
    "Loading sign-in form": "טוען טופס התחברות",
    "Sign in with email": "התחברות עם אימייל",
    "Sign in": "התחברות",
    "Continue": "המשך",
    "Log in": "התחברות",
    "Logout": "התנתקות",
    "Log out": "התנתקות",
    "Create account": "יצירת חשבון",
    "Terms of service": "תנאי שירות",
    "Privacy policy": "מדיניות פרטיות"
  };

  function translateRoot(root) {
    if (!root) return;
    var tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT), n, nodes = [];
    while ((n = tw.nextNode())) nodes.push(n);
    nodes.forEach(function (node) {
      var raw = node.nodeValue, key = (raw || "").trim();
      if (key && MAP[key] && key !== MAP[key]) node.nodeValue = raw.replace(key, MAP[key]);
    });
    if (root.querySelectorAll) {
      root.querySelectorAll("*").forEach(function (e) {
        ["placeholder", "aria-label", "title", "alt"].forEach(function (a) {
          var v = e.getAttribute && e.getAttribute(a);
          if (v && MAP[v.trim()]) e.setAttribute(a, MAP[v.trim()]);
        });
        if (e.shadowRoot) { translateRoot(e.shadowRoot); observe(e.shadowRoot); }
      });
    }
  }

  var seen = (typeof WeakSet !== "undefined") ? new WeakSet() : null;
  function observe(root) {
    if (!root || (seen && seen.has(root))) return;
    if (seen) seen.add(root);
    try {
      new MutationObserver(function () { translateRoot(root); }).observe(root, {
        childList: true, subtree: true, characterData: true,
        attributes: true, attributeFilter: ["placeholder", "aria-label", "title", "alt"]
      });
    } catch (e) {}
  }

  var SHADOW_CSS = [
    '[part="dialog"],[part="dialog"] *{direction:rtl}',
    '[part="dialog"] input{text-align:right}',
    '.account__close{border:none!important;background:transparent!important;box-shadow:none!important;color:#8a8a8a!important}',
    '.account__close:hover{color:#111!important;background:rgba(0,0,0,.06)!important}',
    '.account__heading{font-size:17px!important;font-weight:700!important;direction:rtl!important;text-align:center!important;justify-content:center!important;width:100%!important}',
    /* Title CENTERED with the close X pinned to the RIGHT (Ido): center the header content and
       absolutely position the X at the right edge so it overlays without shifting the title. */
    '.account__header{padding-bottom:4px!important;position:relative!important;direction:rtl!important;justify-content:center!important;align-items:center!important}',
    '.account__header-content{width:100%!important;align-items:center!important}',
    '.account__close{position:absolute!important;top:50%!important;transform:translateY(-50%)!important;right:8px!important;left:auto!important;inset-inline-start:auto!important;inset-inline-end:auto!important}',
    '.account__menu-items nav,nav.stack-inline{display:flex!important;gap:8px!important}'
  ].join('');

  function boot() {
    var els = document.querySelectorAll("shopify-account");
    if (!els.length) return false;
    var any = false;
    els.forEach(function (el) {
      if (!el.shadowRoot) return;
      any = true;
      translateRoot(el.shadowRoot);
      observe(el.shadowRoot);
      try {
        if (!el.shadowRoot.querySelector("style[data-he-rtl]")) {
          var s = document.createElement("style");
          s.setAttribute("data-he-rtl", "");
          s.textContent = SHADOW_CSS;
          el.shadowRoot.appendChild(s);
        }
      } catch (e) {}
    });
    return any;
  }

  var tries = 0, iv = setInterval(function () { if (boot() || ++tries > 40) clearInterval(iv); }, 250);
  document.addEventListener("click", function (e) {
    if (e.target && e.target.closest && e.target.closest("shopify-account")) setTimeout(boot, 250);
  }, true);
})();

/* ============================================================
   CART FREE-SHIPPING BAR updater (Ido, cart plan step 7, 2026-07-02).
   Initial state renders in Liquid (snippets/cart-drawer.liquid); this keeps it live.
   `cart:updated` only fires on qty changes (theme.js:1883) and add-to-cart fires
   `ajaxProduct:added` — so the robust hook is a MutationObserver on the drawer's
   [data-products], which theme.CartForm.buildCart() rewrites on EVERY path
   (add / qty / remove), reading the fresh .cart__items dataset. Money in agorot.
   Revertible: delete this IIFE + the Liquid block + the CSS.
   ============================================================ */
(function () {
  if (window.__cartFreeshipInit) return;
  window.__cartFreeshipInit = true;

  function fmt(cents) {
    var s;
    try { s = theme.Currency.formatMoney(cents, theme.settings.moneyFormat); }
    catch (e) { s = '₪' + (cents / 100).toFixed(2); }
    return s.replace(/(\d)\.00(?!\d)/, '$1');
  }

  function update() {
    // parity (2026-07-02): a bar lives in the DRAWER and on the /cart PAGE — update each from
    // ITS OWN form's fresh .cart__items dataset (each CartForm rebuilds only its own items).
    document.querySelectorAll('[data-freeship]').forEach(function (bar) {
      var form = bar.closest('form');
      var items = (form && form.querySelector('.cart__items')) || document.querySelector('.cart__items');
      // no AJAX-rendered items yet (e.g. /cart page before the first rebuild) —
      // keep the server-rendered Liquid state instead of clobbering it with 0
      if (!items || items.dataset.cartSubtotal === undefined) return;
      var total = parseInt(items.dataset.cartSubtotal || '0', 10);
      var thr = parseInt(bar.dataset.threshold, 10) || 24900;
      var rem = thr - total;
      var pct = Math.max(0, Math.min(100, Math.round(total * 100 / thr)));
      var fill = bar.querySelector('[data-fs-fill]');
      var msg = bar.querySelector('[data-fs-msg]');
      if (fill) fill.style.width = pct + '%';
      // visual audit H1: "achieved" reads as SUCCESS (black fill + ✓), red stays for "still on the way"
      bar.classList.toggle('is-complete', rem <= 0);
      // micro-interaction: a one-shot pulse the moment the shopper CROSSES the threshold
      var prev = parseFloat(bar.dataset.fsPrev);
      bar.dataset.fsPrev = rem;
      if (!isNaN(prev) && prev > 0 && rem <= 0) {
        bar.classList.add('fs-won');
        setTimeout(function () { bar.classList.remove('fs-won'); }, 1400);
      }
      if (msg) {
        msg.innerHTML = rem > 0
          ? 'עוד <b>' + fmt(rem) + '</b> למשלוח חינם 🚚'
          : '✓ זכית במשלוח חינם!';
      }
    });
  }

  function bind() {
    ['#CartDrawerForm [data-products]', '#CartPageForm [data-products]'].forEach(function (sel) {
      var products = document.querySelector(sel);
      if (products) {
        try { new MutationObserver(update).observe(products, { childList: true }); } catch (e) {}
      }
    });
    document.addEventListener('cart:updated', update);
    document.addEventListener('ajaxProduct:added', function () { setTimeout(update, 600); });
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();

/* ============================================================
   CART DRAWER: REMOVE ITEM (X) + UNDO TOAST (Ido, cart plan step 2, 2026-07-02).
   Stock Impulse HIDES the remove link in the drawer (theme.css:12535) because it's a
   plain <a href="/cart/change?...quantity=0"> = full page navigation. We un-hide it
   (CSS) and intercept the click: dispatch the theme's OWN quantity event
   ('cart:quantity.cart-cart-drawer', namespace = '.cart-' + form data-location,
   theme.js:1687/1710) with qty "0" — a STRING, because quantityChanged's
   `if (!key || !qty)` guard would reject a numeric 0 but passes "0" (exactly how the
   theme's own stepper empties a line). That reuses the full native flow:
   changeItem → buildCart → cart:updated. Undo re-adds via /cart/add.js and rebuilds
   via 'ajaxProduct:added' (CartDrawer listens → buildCart, theme.js:4522).
   Revertible: delete this IIFE + the "CART REMOVE X + UNDO TOAST" CSS block.
   ============================================================ */
(function () {
  if (window.__cartRemoveInit) return;
  window.__cartRemoveInit = true;

  var lastRemoved = null, toastEl = null, toastTimer = null;

  function ensureToast() {
    if (toastEl) return toastEl;
    toastEl = document.createElement('div');
    toastEl.className = 'cart-undo-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.innerHTML = '<span>הפריט הוסר מהעגלה</span><button type="button" data-cart-undo>ביטול</button>';
    var drawer = document.getElementById('CartDrawer');
    (drawer || document.body).appendChild(toastEl);
    return toastEl;
  }
  function showToast(fromPage) {
    var t = ensureToast();
    // on /cart the drawer is off-screen — host the toast on <body> as a fixed bar instead
    var host = fromPage ? document.body : (document.getElementById('CartDrawer') || document.body);
    if (t.parentNode !== host) host.appendChild(t);
    t.classList.toggle('cart-undo-toast--page', !!fromPage);
    t.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 5000);
  }
  function hideToast() {
    if (toastEl) toastEl.classList.remove('is-visible');
    lastRemoved = null;
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    var undo = e.target.closest('[data-cart-undo]');
    if (undo) {
      e.preventDefault();
      if (!lastRemoved) { hideToast(); return; }
      var payload = { id: lastRemoved.id, quantity: lastRemoved.quantity };
      if (lastRemoved.properties && Object.keys(lastRemoved.properties).length) payload.properties = lastRemoved.properties;
      var fromPage = lastRemoved.fromPage;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).then(function () {
        hideToast();
        if (fromPage) {
          // only the drawer listens to ajaxProduct:added — on /cart just reload to re-render the page form
          window.location.reload();
        } else {
          document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
        }
      }).catch(function () { hideToast(); });
      return;
    }

    var link = e.target.closest('#CartDrawer .cart__remove a, #CartPageForm .cart__remove a');
    if (!link) return;
    e.preventDefault();
    var item = link.closest('.cart__item');
    var key = item && item.dataset.key;
    if (!key) return;
    // parity (2026-07-02): the /cart page form has its own CartForm with namespace '.cart-page'
    // (theme.js:1687 — '.cart-' + data-location); dispatch on the right one.
    var form = link.closest('form');
    var ns = (form && form.id === 'CartPageForm') ? '.cart-page' : '.cart-cart-drawer';
    item.style.opacity = '.45';
    // capture the line for undo BEFORE removing (variant id + qty + properties)
    theme.cart.getCart().then(function (cart) {
      var line = (cart.items || []).find(function (i) { return i.key === key; });
      if (line) lastRemoved = { id: line.id, quantity: line.quantity, properties: line.properties || null, fromPage: ns === '.cart-page' };
      // native path: string "0" passes the `!qty` guard exactly like the theme's stepper does
      document.dispatchEvent(new CustomEvent('cart:quantity' + ns, { detail: [key, '0', null] }));
      if (line) showToast(ns === '.cart-page');
    }).catch(function () { item.style.opacity = ''; });
  }, false);
})();

/* ============================================================
   IN-CART CROSS-SELL ROW (Ido, cart plan 3.1, 2026-07-02).
   Compact horizontal "אולי יתאים לך גם" row inside the drawer scroll area,
   seeded by the most-recent cart line (cart.js items[0] = newest first).
   INTENT: tries `complementary` (Search & Discovery) first — EMPTY on this
   store until Ido configures complementary products — then falls back to
   `related`, so the row works today and upgrades itself automatically once
   S&D is configured. Dedupes against cart product_ids, skips unavailable,
   caps at 4. Add = /cart/add.js + the theme's own 'ajaxProduct:added'
   (CartDrawer → buildCart, theme.js:4522); the row lives OUTSIDE
   [data-products] so rebuilds don't wipe it, and refreshes only when the
   cart's product-id set actually changes. Revertible: delete this IIFE +
   the "CART UPSELL ROW" CSS block.
   ============================================================ */
(function () {
  if (window.__cartUpsellInit) return;
  window.__cartUpsellInit = true;

  var lastKey = null, container = null;

  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); }
  function money(c) {
    var s;
    try { s = theme.Currency.formatMoney(c, theme.settings.moneyFormat); }
    catch (e) { s = '₪' + (c / 100).toFixed(2); }
    return s.replace(/(\d)\.00(?!\d)/, '$1'); // 270.00→270; 127.09 stays (Ido 2026-07-03)
  }
  function thumb(url) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=216';
  }

  function ensure() {
    var scroll = document.querySelector('#CartDrawerForm .drawer__scrollable');
    if (!scroll) return null;
    if (container && scroll.contains(container)) return container;
    container = document.createElement('div');
    container.className = 'cart-upsell hide';
    container.innerHTML = '<div class="cart-upsell__title">אולי יתאים לך גם</div><div class="cart-upsell__row" data-upsell-row></div>';
    var products = scroll.querySelector('[data-products]');
    if (products && products.nextSibling) scroll.insertBefore(container, products.nextSibling);
    else scroll.appendChild(container);
    return container;
  }

  var productMap = {};   // pid -> pick (for the colour-picker sheet)

  /* desktop reachability (Ido 2026-07-06, cart-drawer UX audit): the row is a
     touch strip with a deliberately hidden scrollbar — a mouse user had NO way
     to reach the hidden cards (wheel scrolls vertically, drag isn't native).
     Adds: wheel→horizontal mapping, mouse drag-to-scroll, and the site's slim
     progress bar (same .cart-vpick__ind pattern as the colour strips). */
  var upsellDrag = null, upsellDragAt = 0;
  function attachScroll(c, row) {
    var ind = c.querySelector('.cart-vpick__ind');
    if (!ind) {
      ind = document.createElement('div');
      ind.className = 'cart-vpick__ind';
      ind.innerHTML = '<div class="cart-vpick__ind-thumb"></div>';
      c.appendChild(ind);
    }
    var thumb = ind.firstChild;
    function sync() {
      var over = row.scrollWidth - row.clientWidth;
      if (over < 8) { ind.style.display = 'none'; return; }
      ind.style.display = '';
      thumb.style.width = Math.max(14, Math.round(row.clientWidth / row.scrollWidth * 100)) + '%';
      var maxLeft = ind.clientWidth - thumb.offsetWidth;
      var pr = Math.min(1, Math.abs(row.scrollLeft) / over);
      thumb.style.insetInlineStart = Math.round(pr * maxLeft) + 'px';
    }
    row.addEventListener('scroll', sync, { passive: true });
    sync();
    setTimeout(sync, 400); /* images settle after the AJAX rebuild */
    if (row.dataset.upsellScroll) return;
    row.dataset.upsellScroll = '1';
    row.addEventListener('wheel', function (e) {
      if (row.scrollWidth - row.clientWidth < 8) return;
      var d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (!d) return;
      e.preventDefault();
      /* RTL: forward = negative scrollLeft */
      row.scrollLeft += (getComputedStyle(row).direction === 'rtl' ? -d : d);
    }, { passive: false });
    row.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;
      upsellDrag = { row: row, x: e.clientX, sl: row.scrollLeft, moved: false };
    });
    row.addEventListener('click', function (e) {
      /* a drag that ends on a link must not navigate */
      if (Date.now() - upsellDragAt < 300) { e.preventDefault(); e.stopPropagation(); }
    }, true);
  }
  window.addEventListener('pointermove', function (e) {
    if (!upsellDrag) return;
    var dx = e.clientX - upsellDrag.x;
    if (Math.abs(dx) > 4) upsellDrag.moved = true;
    upsellDrag.row.scrollLeft = upsellDrag.sl - dx;
  });
  window.addEventListener('pointerup', function () {
    if (upsellDrag && upsellDrag.moved) upsellDragAt = Date.now();
    upsellDrag = null;
  });

  function render(list) {
    var c = ensure();
    if (!c) return;
    closePicker();       // any open sheet holds stale data after a re-render
    var row = c.querySelector('[data-upsell-row]');
    if (!list.length) { c.classList.add('hide'); row.innerHTML = ''; return; }
    productMap = {};
    row.innerHTML = list.map(function (p) {
      // Ido 2026-07-03 (truncated titles, no extra height): the separate price row was
      // traded for a 3rd title line — the price now lives INSIDE the add button.
      var label = '+ הוספה · ' + money(p.price);
      productMap[p.pid] = p;
      var btn = (p.variants && p.variants.length > 1)
        ? '<button type="button" class="cart-upsell__add" data-upsell-pick="' + p.pid + '">' + label + '</button>'
        : '<button type="button" class="cart-upsell__add" data-upsell-add="' + p.vid + '">' + label + '</button>';
      return '<div class="cart-upsell__card">'
        + '<a href="' + esc(p.url) + '" class="cart-upsell__img"><img src="' + esc(thumb(p.img)) + '" alt="' + esc(p.title) + '" loading="lazy"></a>'
        + '<a href="' + esc(p.url) + '" class="cart-upsell__name">' + esc(p.title) + '</a>'
        + btn
        + '</div>';
    }).join('');
    c.classList.remove('hide');
    attachScroll(c, row);
  }

  /* ---- colour-picker bottom sheet (multi-variant add) ----
     Two hosts (Ido, 2026-07-03): the drawer sheet lives INSIDE #CartDrawer
     (absolute to it), but on the /cart PAGE the drawer exists too — closed and
     off-screen — so the page gets its OWN fixed-position sheet on <body>
     (.cart-vpick--page). sheetCtx picks the host; swapNs picks the CartForm
     event namespace (theme.js data-location: cart-drawer vs page). */
  var sheets = {};
  var sheetCtx = 'drawer';
  var swapNs = '.cart-cart-drawer';

  /* page-sheet backdrop (Ido, 2026-07-05): tapping OUTSIDE the colour window
     closes it (mobile expectation for bottom sheets) */
  var backdropEl = null;
  function pageBackdrop(on) {
    if (on && !backdropEl) {
      backdropEl = document.createElement('div');
      backdropEl.className = 'cart-vpick-backdrop';
      backdropEl.addEventListener('click', function () { closePicker(); });
      document.body.appendChild(backdropEl);
    }
    if (backdropEl) backdropEl.classList.toggle('is-open', !!on);
  }

  function ensureSheet() {
    if (sheets[sheetCtx]) return sheets[sheetCtx];
    var el = document.createElement('div');
    el.className = 'cart-vpick' + (sheetCtx === 'page' ? ' cart-vpick--page' : '');
    el.innerHTML = '<div class="cart-vpick__head">'
      + '<span class="cart-vpick__title" data-vpick-title>בחר צבע</span>'
      + '<button type="button" class="cart-vpick__close" data-vpick-close aria-label="סגירה">✕</button>'
      + '</div><div class="cart-vpick__row" data-vpick-row></div>';
    if (sheetCtx === 'page') {
      document.body.appendChild(el);
    } else {
      var drawer = document.getElementById('CartDrawer');
      (drawer || document.body).appendChild(el);
    }
    sheets[sheetCtx] = el;
    return el;
  }
  // generic sheet: chips = [{id, name, img, current}], onPick(id, btnEl) or null (view-only)
  var sheetOnPick = null;
  function openSheet(title, chips, onPick) {
    var s = ensureSheet();
    sheetOnPick = onPick || null;
    var oldBar = s.querySelector('.cart-vpick__confirm');
    if (oldBar) oldBar.remove();
    var rowEl = s.querySelector('[data-vpick-row]');
    if (rowEl) rowEl.classList.remove('has-pick');
    s.querySelector('[data-vpick-title]').textContent = title;
    s.querySelector('[data-vpick-row]').innerHTML = chips.map(function (v) {
      return '<button type="button" class="cart-vpick__chip' + (v.current ? ' is-current' : '') + '"'
        + ' data-vpick-add="' + v.id + '"' + (v.current ? ' disabled' : '') + '>'
        + '<img src="' + esc(thumb(v.img)) + '" alt="" loading="lazy">'
        + '<span>' + esc(v.name) + '</span>'
        + '</button>';
    }).join('');
    s.classList.add('is-open');
    if (sheetCtx === 'page') pageBackdrop(true);
    sheetIndicator(s);
  }

  /* many colours (Ido, 2026-07-05): the chip row scrolls sideways — give it
     the same slim indicator the PDP colour strip has, so off-screen colours
     are discoverable. Rebuilt per open (chip count changes per product). */
  function sheetIndicator(s) {
    var row = s.querySelector('[data-vpick-row]');
    if (!row) return;
    var ind = s.querySelector('.cart-vpick__ind');
    if (!ind) {
      ind = document.createElement('div');
      ind.className = 'cart-vpick__ind';
      ind.innerHTML = '<div class="cart-vpick__ind-thumb"></div>';
      row.insertAdjacentElement('afterend', ind);
      var thumbEl = ind.firstChild;
      var update = function () {
        var over = row.scrollWidth - row.clientWidth;
        if (over < 6) { ind.style.display = 'none'; return; }
        ind.style.display = '';
        var ratio = row.clientWidth / row.scrollWidth;
        thumbEl.style.width = Math.max(14, Math.round(ratio * 100)) + '%';
        var maxLeft = ind.clientWidth - thumbEl.offsetWidth;
        var progress = Math.min(1, Math.abs(row.scrollLeft) / over); /* RTL scrollLeft negative */
        thumbEl.style.insetInlineStart = Math.round(progress * maxLeft) + 'px';
      };
      row.addEventListener('scroll', update, { passive: true });
      ind.__update = update;
    }
    /* defer one frame so chip images/layout settle before measuring */
    requestAnimationFrame(function () { if (ind.__update) ind.__update(); });
    setTimeout(function () { if (ind.__update) ind.__update(); }, 400);
  }
  function openPicker(pid) {
    var p = productMap[pid];
    if (!p || !p.variants) return;
    sheetCtx = 'drawer'; swapNs = '.cart-cart-drawer';   // upsell row lives in the drawer only
    openSheet('בחר צבע · ' + p.title, p.variants, function (id, btn) { addVariant(id, btn); });
  }
  function closePicker() {
    Object.keys(sheets).forEach(function (k) { sheets[k].classList.remove('is-open'); });
    pageBackdrop(false);
  }

  /* bridge for the PDP sticky bar (3.2): same sheet, page context.
     addVariant is hoisted within this IIFE. */
  window.__tikSheet = {
    open: function (title, chips, onPick) { sheetCtx = 'page'; openSheet(title, chips, onPick); },
    close: closePicker,
    add: function (id, btn) { addVariant(id, btn); }
  };

  /* ---- cart-line colour SWAP (Ido, 2026-07-03): tapping the "צבע: X" line of a
     cart item opens the SAME sheet with the product's available colours (current
     one marked); picking swaps the variant keeping quantity + properties.
     Data from /products/{handle}.js; add-new-then-remove-old (safer if the new
     colour lacks stock — nothing is lost), removal via the theme's own
     cart:quantity event (string '0'). ---- */
  // moveQty of totalQty switch colour; the rest (if any) stays on the old line.
  // Add-new-FIRST (stock check — nothing lost on failure), then shrink/remove old
  // via the theme's own cart:quantity event (string qty passes the !qty guard).
  function swapLine(oldKey, newId, btn, moveQty, totalQty) {
    if (btn) btn.disabled = true;
    theme.cart.getCart().then(function (cart) {
      var line = (cart.items || []).find(function (i) { return i.key === oldKey; });
      if (!line) { closePicker(); return; }
      var n = Math.min(moveQty || line.quantity, line.quantity);
      var payload = { id: newId, quantity: n };
      if (line.properties && Object.keys(line.properties).length) payload.properties = line.properties;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || j.message || 'שגיאה'); });
        var remaining = line.quantity - n;
        document.dispatchEvent(new CustomEvent('cart:quantity' + swapNs, { detail: [oldKey, String(remaining), null] }));
        closePicker();
      });
    }).catch(function (err) {
      if (btn) btn.disabled = false;
      alert(err && err.message ? err.message : 'לא ניתן להחליף צבע כרגע');
    });
  }

  // qty>1 (Ido, 2026-07-03): picking a colour reveals a confirm bar in the SAME sheet —
  // how many of the line to switch (default: all; lower it to split the line).
  // the red frame follows the selection (Ido): mark the picked chip + flag the row
  function markPicked(newId) {
    var s = ensureSheet();
    var row = s.querySelector('[data-vpick-row]');
    if (row) row.classList.add('has-pick');
    s.querySelectorAll('.cart-vpick__chip').forEach(function (c) {
      c.classList.toggle('is-picked', parseInt(c.dataset.vpickAdd, 10) === newId);
    });
  }

  function showSwapConfirm(oldKey, newId, totalQty) {
    var s = ensureSheet();
    markPicked(newId);
    var bar = s.querySelector('.cart-vpick__confirm');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'cart-vpick__confirm';
      s.appendChild(bar);
    }
    var n = totalQty;
    bar.innerHTML = '<div class="cart-vpick__qty">'
      + '<button type="button" data-sw-minus aria-label="פחות">−</button>'
      + '<span data-sw-n>' + n + '</span>'
      + '<button type="button" data-sw-plus aria-label="יותר">+</button>'
      + '</div>'
      + '<div class="cart-vpick__of">מתוך ' + totalQty + ' בעגלה</div>'
      + '<button type="button" class="cart-vpick__go" data-sw-go>החלפה</button>';
    bar.onclick = function (ev) {
      if (ev.target.closest('[data-sw-minus]')) {
        n = Math.max(1, n - 1);
        bar.querySelector('[data-sw-n]').textContent = n;
      } else if (ev.target.closest('[data-sw-plus]')) {
        n = Math.min(totalQty, n + 1);
        bar.querySelector('[data-sw-n]').textContent = n;
      } else if (ev.target.closest('[data-sw-go]')) {
        var go = ev.target.closest('[data-sw-go]');
        go.disabled = true;
        swapLine(oldKey, newId, go, n, totalQty);
      }
    };
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var v = e.target.closest('#CartDrawer .cart__item--variants, #CartPageForm .cart__item--variants');
    if (!v) return;
    /* context routing: the /cart page form uses the .cart-page namespace and
       the fixed body-level sheet; the drawer keeps its own */
    if (v.closest('#CartPageForm')) { sheetCtx = 'page'; swapNs = '.cart-page'; }
    else { sheetCtx = 'drawer'; swapNs = '.cart-cart-drawer'; }
    var item = v.closest('.cart__item');
    var key = item && item.dataset.key;
    if (!key) return;
    var link = item.querySelector('.cart__item-name');
    var path;
    try { path = new URL(link.href, location.origin).pathname; } catch (err) { return; }
    if (path.indexOf('/products/') < 0) return;
    var curVid = parseInt(key.split(':')[0], 10);
    fetch(path + '.js').then(function (r) { return r.json(); }).then(function (prod) {
      var avail = (prod.variants || []).filter(function (vv) { return vv.available || vv.id === curVid; });
      var chips = avail.map(function (vv) {
        return {
          id: vv.id,
          name: vv.option1 || vv.title,
          img: (vv.featured_image && vv.featured_image.src) || (prod.images && prod.images[0]) || '',
          current: vv.id === curVid
        };
      });
      if (chips.length < 2) {
        openSheet('זה הצבע היחיד הזמין · ' + prod.title, chips, null);
        return;
      }
      var lineQty = parseInt((item.querySelector('.js-qty__num') || {}).value || '1', 10) || 1;
      openSheet('החלפת צבע · ' + prod.title, chips, function (id, btn) {
        markPicked(id);   // red frame jumps to the chosen colour immediately
        if (lineQty <= 1) { swapLine(key, id, btn, 1, 1); return; }
        showSwapConfirm(key, id, lineQty);   // qty>1: choose how many to switch
      });
    }).catch(function () {});
  }, false);

  function pick(prods, idSet, titleSet) {
    var picks = [];
    (prods || []).forEach(function (x) {
      if (picks.length >= 4 || !x.available) return;
      if (idSet.indexOf(x.id) >= 0) return;
      // the catalog has DUPLICATE listings (same title, different id) — an id-only
      // dedupe can still show a "copy" of a cart item, so belt with a title dedupe
      if (titleSet.indexOf((x.title || '').trim()) >= 0) return;
      // keep ALL available variants — a multi-colour product opens the colour picker
      // sheet instead of blind-adding the first colour (sold-out colours hidden,
      // consistent with the catalog rule)
      var avail = (x.variants || []).filter(function (vv) { return vv.available; });
      if (!avail.length) return;
      picks.push({
        pid: x.id, url: x.url, title: x.title, price: x.price, vid: avail[0].id, img: x.featured_image,
        variants: avail.map(function (vv) {
          return { id: vv.id, name: vv.option1 || vv.title, img: (vv.featured_image && vv.featured_image.src) || x.featured_image };
        })
      });
    });
    return picks;
  }

  var bootTries = 0;
  function refresh() {
    // custom.js executes BEFORE theme.js (defer order, theme.liquid:46 vs 340) —
    // theme.cart doesn't exist yet on the very first calls; retry briefly.
    if (!window.theme || !theme.cart || !theme.cart.getCart) {
      if (bootTries++ < 20) setTimeout(refresh, 300);
      return;
    }
    theme.cart.getCart().then(function (cart) {
      if (!cart.items || !cart.items.length) { render([]); lastKey = null; return; }
      var idSet = cart.items.map(function (i) { return i.product_id; });
      var titleSet = cart.items.map(function (i) { return (i.product_title || '').trim(); });
      var key = idSet.slice().sort().join(',');
      if (key === lastKey) return;
      lastKey = key;
      var seed = cart.items[0].product_id; // newest line first in /cart.js
      fetch('/recommendations/products.json?product_id=' + seed + '&limit=8&intent=complementary')
        .then(function (r) { return r.json(); })
        .then(function (j) {
          if (j.products && j.products.length) return j.products;
          return fetch('/recommendations/products.json?product_id=' + seed + '&limit=8&intent=related')
            .then(function (r) { return r.json(); })
            .then(function (j2) { return j2.products || []; });
        })
        .then(function (prods) { render(pick(prods, idSet, titleSet)); })
        .catch(function () {});
    }).catch(function () {});
  }

  function addVariant(id, btn) {
    var orig = null;
    if (btn) {
      btn.disabled = true;
      // micro-interaction (Ido GO 2026-07-02): instant "✓ נוסף" feedback bridges the AJAX wait;
      // the row is rebuilt right after (added product leaves it), so no restore needed on success
      if (btn.classList.contains('cart-upsell__add')) {
        orig = btn.innerHTML;
        btn.classList.add('is-added');
        btn.innerHTML = '✓ נוסף';
      }
    }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: id, quantity: 1 })
    }).then(function (res) {
      /* a 4xx/5xx also resolves — without this check the button lied "✓ נוסף"
         while nothing was added (2026-07-06 bug hunt) */
      if (!res.ok) throw new Error('add failed ' + res.status);
      closePicker();
      document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
    }).catch(function () {
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('is-added');
        if (orig) {
          btn.innerHTML = 'שגיאה, לנסות שוב';
          setTimeout(function () { btn.innerHTML = orig; }, 2600);
        }
      }
    });
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var pickBtn = e.target.closest('[data-upsell-pick]');
    if (pickBtn) { e.preventDefault(); openPicker(parseInt(pickBtn.dataset.upsellPick, 10)); return; }
    var chip = e.target.closest('[data-vpick-add]');
    if (chip) {
      e.preventDefault();
      if (chip.disabled) return;
      var act = sheetOnPick || addVariant;   // upsell sets addVariant; cart-swap sets swapLine
      act(parseInt(chip.dataset.vpickAdd, 10), chip);
      return;
    }
    if (e.target.closest('[data-vpick-close]')) { e.preventDefault(); closePicker(); return; }
    var btn = e.target.closest('[data-upsell-add]');
    if (btn) { e.preventDefault(); addVariant(parseInt(btn.dataset.upsellAdd, 10), btn); }
  }, false);

  function bind() {
    var products = document.querySelector('#CartDrawerForm [data-products]');
    if (products) {
      try { new MutationObserver(function () { setTimeout(refresh, 60); }).observe(products, { childList: true }); } catch (e) {}
    }
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();



/* ============================================================
   CHECKOUT CTA — STUCK-SPINNER FIX (Ido, 2026-07-02, simplified).
   The theme ALREADY shows its own spinner on submit (CartForm.onSubmit adds
   `btn--loading`, theme.js:1731) — no custom loading layer needed. But the theme
   never clears it when the shopper comes BACK from checkout via bfcache, leaving
   a stuck spinner on the button. This clears it on pageshow. Double-tap guard is
   pure CSS on the theme's own class. Revertible: delete this IIFE + the CSS.
   ============================================================ */
(function () {
  if (window.__cartCtaReset) return;
  window.__cartCtaReset = true;
  window.addEventListener('pageshow', function () {
    document.querySelectorAll('.cart__checkout.btn--loading').forEach(function (b) {
      b.classList.remove('btn--loading');
    });
  });
})();

/* ============================================================
   EMPTY-CART CONTINUE BUTTONS (Ido, 2026-07-02).
   Drawer: "המשך בקנייה" just CLOSES the drawer (the shopper stays on the page
   they were browsing) via the theme's own cart:close event (theme.js:4529).
   /cart page: "חזרה לקנייה" goes BACK when there is a same-site page to go back
   to, else falls through to the href (/collections/all). Revertible.
   ============================================================ */
(function () {
  if (window.__cartEmptyNav) return;
  window.__cartEmptyNav = true;
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var cont = e.target.closest('[data-cart-continue]');
    if (cont) {
      e.preventDefault();
      document.dispatchEvent(new CustomEvent('cart:close'));
      return;
    }
    var back = e.target.closest('[data-cart-back]');
    if (back) {
      var sameSite = document.referrer && document.referrer.indexOf(location.host) > -1;
      if (sameSite && window.history.length > 1) {
        e.preventDefault();
        window.history.back();
      }
      // else: let the href navigate to /collections/all
    }
  }, false);
})();

/* ============================================================
   CART COUPON FIELD (Ido GO, 2026-07-02). Sits 50/50 next to the notes accordion
   (drawer + /cart page). Mechanics: GET /discount/CODE registers the code for the
   session (Shopify applies it at checkout); we then compare cart totals before/after
   — if the cart reflects a new discount → success message + rebuild (savings line
   lights up); if not → Ido's error copy (usually no-stacking, or a gift card that
   belongs in secure checkout). Enter inside the input is intercepted — the cart
   form must NEVER submit from here (that goes to checkout). Revertible.
   ============================================================ */
(function () {
  if (window.__cartCoupon) return;
  window.__cartCoupon = true;

  var ERR = 'שגיאה — לא ניתן להפעיל את הקוד: אין כפל מבצעים. תווי קנייה מזינים בעמוד התשלום המאובטח.';

  function msgEl(scope) { return scope.querySelector('[data-coupon-msg]'); }
  function show(scope, text, ok) {
    var m = msgEl(scope);
    if (!m) return;
    m.textContent = text;
    m.className = 'cart-coupon__msg ' + (ok ? 'cart-coupon__msg--ok' : 'cart-coupon__msg--err');
    try { m.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (e) {}
  }

  // opening the coupon at the bottom of the scroll area leaves the field half-hidden —
  // scroll it into view (toggle doesn't bubble; capture catches it)
  document.addEventListener('toggle', function (e) {
    var d = e.target;
    if (d && d.classList && d.classList.contains('cart-coupon') && d.open) {
      try { d.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (err) {}
    }
  }, true);

  function discountState(cart) {
    var lvl = (cart.cart_level_discount_applications || []).length;
    return { total: cart.total_discount || 0, levels: lvl };
  }

  function apply(scope) {
    var input = scope.querySelector('[data-coupon-input]');
    var btn = scope.querySelector('[data-coupon-apply]');
    var code = input && input.value.trim();
    if (!code) { if (input) input.focus(); return; }
    if (!window.theme || !theme.cart || !theme.cart.getCart) return;
    btn.disabled = true;
    var orig = btn.textContent;
    btn.textContent = 'בודק…';
    theme.cart.getCart().then(function (before) {
      var pre = discountState(before);
      return fetch('/discount/' + encodeURIComponent(code)).then(function () {
        return theme.cart.getCart();
      }).then(function (after) {
        var post = discountState(after);
        if (post.total > pre.total || post.levels > pre.levels) {
          show(scope, 'הקוד ' + code + ' הופעל!', true);
          document.dispatchEvent(new CustomEvent('ajaxProduct:added')); // rebuild → discount + savings rows render
        } else {
          show(scope, ERR, false);
        }
      });
    }).catch(function () {
      show(scope, ERR, false);
    }).then(function () {
      btn.disabled = false;
      btn.textContent = orig;
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-coupon-apply]');
    if (!btn) return;
    e.preventDefault();
    apply(btn.closest('.cart-coupon'));
  }, false);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest && e.target.closest('[data-coupon-input]');
    if (!input) return;
    e.preventDefault(); // never submit the cart form (that would jump to checkout)
    apply(input.closest('.cart-coupon'));
  }, true);

  /* COUPON CHIPS (Ido 2026-07-11): cart.discount_codes exists in /cart.js but
     is NOT exposed to Liquid on this theme's API version (verified live —
     the snippet loop renders nothing), so the chips are injected here from
     getCart() into the .cart__discounts row that cart-discounts.liquid always
     renders. Re-runs after every theme rebuild ([data-discounts] children are
     replaced wholesale — theme.js updateCartDiscounts); the signature guard
     stops observer feedback loops. */
  function escHtml(s) { var d = document.createElement('div'); d.textContent = s; return d.innerHTML; }
  function refreshCoupons() {
    if (!window.theme || !theme.cart || !theme.cart.getCart) return;
    theme.cart.getCart().then(function (c) {
      var codes = c.discount_codes || [];
      var sig = codes.map(function (d) { return d.code + ':' + d.applicable; }).join(',');
      document.querySelectorAll('[data-discounts] .cart__discounts').forEach(function (row) {
        var col = row.querySelector('.cart__discounts-col');
        if (!col) return;
        if (col.getAttribute('data-codes-sig') === sig) return;
        col.setAttribute('data-codes-sig', sig);
        col.querySelectorAll('.cart__coupon-chip, .cart__coupon-na').forEach(function (n) { n.remove(); });
        var html = '';
        codes.forEach(function (d) {
          html += '<div class="cart__coupon-chip' + (d.applicable ? '' : ' cart__coupon-chip--na') + '" data-code="' + escHtml(d.code).replace(/"/g, '&quot;') + '">' +
            '<bdi class="cart__coupon-code">' + escHtml(d.code) + '</bdi>' +
            '<button type="button" class="cart__coupon-remove" data-coupon-remove aria-label="הסרת הקופון ' + escHtml(d.code) + '" title="הסרת הקופון">✕</button></div>';
          if (!d.applicable) html += '<small class="cart__coupon-na">הקוד לא חל על העגלה הנוכחית</small>';
        });
        col.insertAdjacentHTML('afterbegin', html);
        var hasAmounts = !!col.querySelector('.cart__discount--cart');
        row.classList.toggle('hide', codes.length === 0 && !hasAmounts);
      });
    }).catch(function () {});
  }
  var couponsTimer = null;
  function refreshCouponsSoon() {
    clearTimeout(couponsTimer);
    couponsTimer = setTimeout(refreshCoupons, 200);
  }
  function armCouponsObserver() {
    document.querySelectorAll('[data-discounts]').forEach(function (host) {
      if (host.__ttCoupObs) return;
      host.__ttCoupObs = true;
      new MutationObserver(refreshCouponsSoon).observe(host, { childList: true, subtree: true });
    });
    refreshCouponsSoon();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', armCouponsObserver);
  else armCouponsObserver();

  /* COUPON REMOVAL (Ido 2026-07-11): ✕ on the code chips above.
     POST /cart/update.js {discount: "..."} REPLACES the cart's code list
     (empty string clears all — verified live against this store 2026-07-11),
     so removing one code means re-sending the remaining ones. The theme's
     own rebuild then refreshes prices, the discounts row and the savings
     line, and the observer re-injects the remaining chips. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-coupon-remove]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;
    var chip = btn.closest('[data-code]');
    if (chip) chip.classList.add('is-busy');
    var code = chip ? chip.getAttribute('data-code') : '';
    var rest = [].slice.call(document.querySelectorAll('.cart__coupon-chip[data-code]'))
      .map(function (c) { return c.getAttribute('data-code'); })
      .filter(function (c, i, a) { return c && c !== code && a.indexOf(c) === i; })
      .join(',');
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: rest })
    }).then(function () {
      document.dispatchEvent(new CustomEvent('ajaxProduct:added')); // theme rebuild
    }).catch(function () {
      btn.disabled = false;
      if (chip) chip.classList.remove('is-busy');
    });
  }, false);
})();

/* ============================================================
   DESKTOP FILTER SIDEBAR SCROLL INDICATOR (Ido's idea, 2026-07-03).
   See the matching CSS block. A sticky zero-height host keeps the track pinned
   to the sidebar's visible area; the thumb tracks scrollTop. Re-measures on
   scroll/resize and on group expand/collapse (MutationObserver). Shows only
   when the filters actually overflow. Revertible: delete this IIFE + CSS.
   ============================================================ */
(function () {
  if (window.__sbInd) return;
  window.__sbInd = true;

  function setup() {
    if (window.innerWidth < 769) return;
    var sb = document.querySelector('.grid__item--sidebar');
    if (!sb || sb.__ind) return;
    sb.__ind = true;

    var host = document.createElement('div'); host.className = 'sb-ind';
    var track = document.createElement('div'); track.className = 'sb-ind__track';
    var thumb = document.createElement('div'); thumb.className = 'sb-ind__thumb';
    track.appendChild(thumb); host.appendChild(track);
    sb.insertBefore(host, sb.firstChild);

    function upd() {
      var ch = sb.clientHeight, sh = sb.scrollHeight;
      if (sh <= ch + 6) { track.style.display = 'none'; return; }
      track.style.display = 'block';
      track.style.height = (ch - 4) + 'px';
      var th = Math.max(40, (ch - 4) * ch / sh);
      thumb.style.height = th + 'px';
      var maxTop = (ch - 4) - th;
      thumb.style.top = (maxTop * (sb.scrollTop / (sh - ch))) + 'px';
    }

    sb.addEventListener('scroll', upd, { passive: true });
    window.addEventListener('resize', upd);
    try {
      new MutationObserver(upd).observe(sb, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
    } catch (e) {}
    upd();
    setTimeout(upd, 800);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup);
  else setup();
})();

/* ============================================================
   CART IMAGE VISIBILITY FIX (Ido's iPhone find, 2026-07-03). Cart thumbs were
   rendered lazy + AOS-animated (opacity:0 until "scrolled in") — on iOS Safari,
   inside the AJAX-rebuilt scrollable drawer, neither fires reliably and items
   showed empty grey tiles (the DATA is fine — /cart.js carries the image URLs).
   cart-item.liquid now renders them eager; this belt force-finishes the AOS
   state on every cart rebuild for both the drawer and the /cart page.
   Revertible: delete this IIFE + the loading:'eager' param in cart-item.liquid.
   ============================================================ */
(function () {
  if (window.__cartImgFix) return;
  window.__cartImgFix = true;

  function fix() {
    document.querySelectorAll('#CartDrawerForm .cart__image img, #CartPageForm .cart__image img').forEach(function (img) {
      img.loading = 'eager';
      img.style.opacity = '1';
      var host = img.closest('image-element');
      if (host) host.classList.add('aos-animate');
      var wrap = img.closest('.image-wrap');
      if (wrap) wrap.classList.add('loaded');
      // a broken/stalled load: nudge a retry once
      if (img.complete && img.naturalWidth === 0 && img.src && !img.__retried) {
        img.__retried = true;
        var s = img.src; img.src = ''; img.src = s;
      }
    });
  }

  function bind() {
    ['#CartDrawerForm [data-products]', '#CartPageForm [data-products]'].forEach(function (sel) {
      var el = document.querySelector(sel);
      if (el) { try { new MutationObserver(function () { setTimeout(fix, 50); }).observe(el, { childList: true }); } catch (e) {} }
    });
    fix();
    setTimeout(fix, 1200);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();

/* ============================================================
   WHOLE-PRICE DISPLAY, STORE-WIDE (Ido, 2026-07-03): "270.00 ₪" renders as
   "270 ₪" wherever prices appear — cards, PDP, compare-at, ranges, cart,
   upsell — while real agorot (127.09) stay. Same elements, same positions.
   Idempotent text-node rewrite over known price classes; re-runs (debounced)
   on the theme's AJAX events + a childList body observer (text mutations are
   characterData, so our own rewrites can't retrigger it).
   Revertible: delete this IIFE + the two .replace lines in the formatters.
   ============================================================ */
(function () {
  if (window.__moneyTrim) return;
  window.__moneyTrim = true;

  var SEL = [
    '.grid-product__price', '.grid-product__price--original', '.grid-product__price--savings',
    '.product__price', '.product__price--compare', '.product__price-savings', '.price-range',
    '.cart__price', '.cart__price--strikethrough',
    '[data-subtotal]', '.cart__savings', '.cart__discount', '.cart-upsell__add'
  ].join(', ');

  function trimAll() {
    document.querySelectorAll(SEL).forEach(function (el) {
      var tw = document.createTreeWalker(el, NodeFilter.SHOW_TEXT), n;
      while ((n = tw.nextNode())) {
        if (/\d\.00(?!\d)/.test(n.nodeValue)) {
          n.nodeValue = n.nodeValue.replace(/(\d)\.00(?!\d)/g, '$1');
        }
      }
    });
  }

  var pending = null;
  function trim() {
    if (pending) return;
    pending = setTimeout(function () { pending = null; trimAll(); }, 120);
  }

  function bind() {
    ['cart:updated', 'ajaxProduct:added', 'collection:reloaded', 'recommendations:loaded', 'variant:change'].forEach(function (ev) {
      document.addEventListener(ev, trim);
    });
    window.addEventListener('pageshow', trim);
    try { new MutationObserver(trim).observe(document.body, { childList: true, subtree: true }); } catch (e) {}
    trimAll();
    setTimeout(trimAll, 1200);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();

/* ============================================================
   __cartMsgHe (Ido, 2026-07-03): Shopify's cart AJAX API returns stock-limit
   errors in ENGLISH even on a Hebrew storefront (probed live: change.js 422 →
   "Only 30 items were added to your cart due to availability."), and theme.js
   line ~1858 shows them raw via alert(). Wrap window.alert: known stock
   messages → Hebrew, shown in a branded toast instead of the native dialog;
   anything unrecognized falls through to the original alert untouched.
   ============================================================ */
(function __cartMsgHe() {
  var RULES = [
    { re: /^Only (\d+) items? were added to your cart due to availability\.?$/i,
      he: function (m) { return 'נוספו ' + m[1] + ' יחידות בלבד — זה כל המלאי הזמין כרגע לפריט זה.'; } },
    { re: /^All (\d+) (.+) are in your cart\.?$/i,
      he: function (m) { return 'כל המלאי הזמין (' + m[1] + ' יחידות) של ' + m[2] + ' כבר נמצא בעגלה שלך.'; } },
    { re: /^You can(?:no|')?t add more (.+) to the cart\.?$/i,
      he: function (m) { return 'אי אפשר להוסיף עוד — כל המלאי הזמין של ' + m[1] + ' כבר בעגלה שלך.'; } },
    { re: /^You can only add (\d+) (.+) to the cart\.?$/i,
      he: function (m) { return 'ניתן להוסיף לעגלה עד ' + m[1] + ' יחידות של ' + m[2] + '.'; } },
    { re: /is (?:already )?sold out\.?$/i,
      he: function () { return 'הפריט אזל מהמלאי.'; } }
  ];

  function translate(msg) {
    for (var i = 0; i < RULES.length; i++) {
      var m = String(msg).trim().match(RULES[i].re);
      if (m) return RULES[i].he(m);
    }
    return null;
  }

  var toastEl = null, hideTimer = null;
  function toast(text) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'cart-stock-toast';
      toastEl.setAttribute('role', 'alert');
      toastEl.addEventListener('click', function () { toastEl.classList.remove('is-visible'); });
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = text;
    toastEl.classList.add('is-visible');
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(function () { toastEl.classList.remove('is-visible'); }, 5000);
  }

  var nativeAlert = window.alert.bind(window);
  window.alert = function (msg) {
    var he = translate(msg);
    if (he) { toast(he); return; }
    nativeAlert(msg);
  };
})();

/* ============================================================
   __qtySelectOnTap (Ido, 2026-07-03): tapping the qty NUMBER in a stepper
   should pre-select the current value so the shopper can type the new amount
   straight away, without manually selecting first. focusin + deferred select()
   (iOS needs the defer), plus a one-shot mouseup/touchend guard so the tap
   that focused the field doesn't collapse the selection again.
   ============================================================ */
(function __qtySelectOnTap() {
  var guard = null;
  document.addEventListener('focusin', function (e) {
    var el = e.target;
    if (!el || !el.classList || !el.classList.contains('js-qty__num')) return;
    setTimeout(function () { try { el.select(); } catch (err) {} }, 0);
    guard = el;
    setTimeout(function () { guard = null; }, 400);
  });
  ['mouseup', 'touchend'].forEach(function (ev) {
    document.addEventListener(ev, function (e) {
      if (guard && e.target === guard) { e.preventDefault(); guard = null; }
    });
  });
})();

/* ============================================================
   __pdpMobile (Ido GO, 2026-07-03) — companions to the MOBILE PDP REDESIGN
   CSS block: (1) scroll indicator under the colour strip (only when it
   actually overflows); (2) auto-scroll the selected chip into view;
   (3) move the SKU below the description (Ido: "ה-sku תשים מתחת לתיאור");
   (4) keep the (-N%) discount span in sync on variant:change.
   Mobile PDP only; bails instantly elsewhere.
   ============================================================ */
(function __pdpMobile() {
  function isMobilePdp() {
    return document.body.classList.contains('template-product') &&
      window.matchMedia('(max-width: 768px)').matches;
  }

  function buildIndicator(wrap) {
    if (wrap.dataset.pdpInd) return;
    wrap.dataset.pdpInd = '1';
    var ind = document.createElement('div');
    ind.className = 'pdp-swatch-ind';
    ind.innerHTML = '<div class="pdp-swatch-ind__thumb"></div>';
    wrap.insertAdjacentElement('afterend', ind);
    var thumb = ind.firstChild;
    function update() {
      var over = wrap.scrollWidth - wrap.clientWidth;
      if (over < 6) { ind.style.display = 'none'; return; }
      ind.style.display = '';
      var ratio = wrap.clientWidth / wrap.scrollWidth;
      thumb.style.width = Math.max(14, Math.round(ratio * 100)) + '%';
      var maxLeft = ind.clientWidth - thumb.offsetWidth;
      var progress = Math.min(1, Math.abs(wrap.scrollLeft) / over); /* RTL scrollLeft is negative */
      /* RTL: progress 0 = fully right */
      thumb.style.insetInlineStart = Math.round(progress * maxLeft) + 'px';
    }
    wrap.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
    setTimeout(update, 600); /* after images/fonts settle */
  }

  function scrollSelectedIntoView(wrap) {
    var checked = wrap.querySelector('input:checked');
    var cell = checked && checked.closest('.variant-input');
    if (cell && cell.scrollIntoView) {
      try { cell.scrollIntoView({ inline: 'center', block: 'nearest' }); } catch (e) {}
    }
  }

  /* Ido 2026-07-03: in-stock colours always FIRST (inline-start = right in RTL);
     sold-out chips are pushed to the far end (left) in their original relative
     order. DOM reorder because Chrome cannot flex/grid a <fieldset>. */
  function sortStrip(wrap) {
    var cells = Array.prototype.slice.call(wrap.querySelectorAll('.variant-input'));
    var out = cells.filter(function (c) { return c.querySelector('label.disabled'); });
    if (out.length && out.length < cells.length) {
      out.forEach(function (c) { wrap.appendChild(c); });
    }
  }

  /* Ido GO 1.3 (2026-07-03): long descriptions get a fade + "קרא עוד" toggle —
     the start stays visible (NOT an accordion). Recalibrated twice (Ido
     2026-07-05/06): trigger 160 + clamp 100 — the gap between the two is the
     GUARANTEED minimum reveal (~2.5 lines), so the button never feels dead;
     the earlier 175/145 pair could reveal a useless ~30-45px. */
  function clampDescription() {
    var rte = document.querySelector('[data-product-blocks] .product-block > .rte:not(.product__policies)');
    if (!rte || rte.dataset.pdpClamped) return;
    /* fonts load late and grow the text — this is re-invoked after fonts.ready,
       so only mark done once actually clamped */
    if (rte.scrollHeight <= 160) return;
    rte.dataset.pdpClamped = '1';
    rte.classList.add('pdp-desc-clamp');
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pdp-desc-toggle';
    /* infinitive form = gender-neutral Hebrew (Ido 2026-07-04) */
    btn.textContent = 'לקרוא עוד';
    rte.insertAdjacentElement('afterend', btn);
    btn.addEventListener('click', function () {
      var open = btn.classList.toggle('is-open');
      rte.classList.toggle('pdp-desc-clamp', !open);
      btn.textContent = open ? 'להציג פחות' : 'לקרוא עוד';
      if (!open) rte.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  }

  function pctUpdate(evt) {
    var el = document.querySelector('[data-price-percent]');
    var v = evt && evt.detail && evt.detail.variant;
    if (!el || !v) return;
    if (v.compare_at_price && v.compare_at_price > v.price) {
      var pct = Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price);
      el.textContent = '(-' + pct + '%)';
      el.classList.remove('hide');
    } else {
      el.textContent = '';
      el.classList.add('hide');
    }
  }

  /* Ido 2026-07-03: the low-stock indicator lives ON the colour-label row
     (stock is per-colour). The theme's own <li> is moved (not cloned) so
     theme.js keeps updating its text/class on variant:change. */
  function moveLowStock(colourWrapper, root) {
    /* scope matters: with a quick-shop modal open on a PDP, a global lookup
       could steal the PAGE's inventory line into the modal */
    var scope = (root && root.querySelector) ? root : document;
    var inv = scope.querySelector('[data-product-inventory]');
    var li = inv && inv.closest('.sales-point');
    var block = inv && inv.closest('.product-block--sales-point');
    if (!li || !colourWrapper || colourWrapper.querySelector('.pdp-lowstock')) return;
    li.classList.add('pdp-lowstock');
    colourWrapper.insertBefore(li, colourWrapper.firstChild);
    if (block) block.style.display = 'none';
  }

  /* shared per-scope enhancement — runs on the PDP at boot and inside a
     quick-shop modal when it opens (Ido 2026-07-07: the modal carries the
     template-product class so the CSS applies; this wires the JS half) */
  function enhanceWraps(root, mobile) {
    root.querySelectorAll('.variant-input-wrap').forEach(function (wrap) {
      if (!wrap.querySelector('.color-swatch')) return;
      sortStrip(wrap);
      if (mobile) {
        buildIndicator(wrap);
        scrollSelectedIntoView(wrap);
      }
      moveLowStock(wrap.closest('.variant-wrapper'), root);
      /* truncated colour names get a native tooltip (scan low finding) */
      wrap.querySelectorAll('.variant-input').forEach(function (cell) {
        var label = cell.querySelector('label.color-swatch');
        if (label && !label.title) label.title = cell.dataset.value || '';
      });
    });
  }

  function init() {
    /* desktop adaptation (Ido GO 2026-07-04): sorting, low-stock chip, SKU move
       and the percent sync now run on BOTH viewports; the scroll indicator,
       auto-scroll and read-more clamp stay mobile-only. */
    if (document.body.classList.contains('template-product')) {
      var mobile = window.matchMedia('(max-width: 768px)').matches;
      enhanceWraps(document, mobile);
      if (mobile) {
        clampDescription();
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { setTimeout(clampDescription, 120); });
        setTimeout(clampDescription, 1600);
      }
    }
    /* quick-shop modal (desktop-only theme feature): enhance on open — the
       content arrives via AJAX, so run after the open click settles (twice:
       fast path + slow-network safety) */
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest || !e.target.closest('.quick-product__btn')) return;
      [400, 1400].forEach(function (ms) {
        setTimeout(function () {
          document.querySelectorAll('.modal--quick-shop.modal--is-active').forEach(function (modal) {
            enhanceWraps(modal, false);
          });
        }, ms);
      });
    }, true);
    /* UNgated: the selector below only matches inside .template-product scopes
       (the PDP page or an open quick-shop modal) */
    document.addEventListener('variant:change', function (evt) {
      pctUpdate(evt);
      /* dynamic-variants can re-flag availability — keep in-stock-first order */
      document.querySelectorAll('.template-product .variant-input-wrap').forEach(function (wrap) {
        if (wrap.querySelector('.color-swatch')) sortStrip(wrap);
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
   __rvParity — 2.5 (Ido GO, 2026-07-04): recently-viewed cards match the
   recommendation cards. (1) theme.buildProductGridItem already supports a
   price row but gates it behind the predictive-search setting — flip the
   runtime flag (also enriches predictive-search results, a bonus);
   (2) after the grid renders, enrich sale items from the product JSON the
   theme already cached: red price + struck compare-at + the collection-card
   "-N%" badge classes. Swatch dots are NOT possible here — the metaobject
   hexes are not exposed in /products/{handle}.js.
   ============================================================ */
(function __rvParity() {
  function whenTheme(cb, tries) {
    if (window.theme && theme.settings) return cb();
    if ((tries || 0) > 40) return;
    setTimeout(function () { whenTheme(cb, (tries || 0) + 1); }, 300);
  }
  var rvCache = {};
  function enrich(host) {
    var info = (window.theme && theme.recentlyViewed && theme.recentlyViewed.productInfo) || {};
    host.querySelectorAll('.grid-product').forEach(function (card) {
      if (card.dataset.rvDone) return;
      var link = card.querySelector('a.grid-product__link');
      var href = link ? (link.getAttribute('href') || '') : '';
      var m = href.match(/\/products\/([^?\/]+)/);
      if (!m) return;
      var handle = decodeURIComponent(m[1]);
      var p = info[handle] || rvCache[handle];
      /* theme.js caches the product JSON as raw TEXT keyed by undefined
         (captureProductDetails reads .handle off a string) — fetch ourselves */
      if (!p) {
        if (card.dataset.rvFetching) return;
        card.dataset.rvFetching = '1';
        fetch('/products/' + encodeURIComponent(handle) + '.js')
          .then(function (r) { return r.json(); })
          .then(function (pj) { rvCache[handle] = pj; delete card.dataset.rvFetching; enrich(host); })
          .catch(function () {});
        return;
      }
      if (typeof p === 'string') { try { p = JSON.parse(p); } catch (e) { return; } }
      card.dataset.rvDone = '1';
      if (!(p.compare_at_price_max > p.price_min)) return;
      var priceDiv = card.querySelector('.grid-product__price');
      if (priceDiv) {
        priceDiv.innerHTML = '<span class="grid-product__price--original">'
          + theme.Currency.formatMoney(p.compare_at_price_max, theme.moneyFormat) + '</span> '
          + theme.Currency.formatMoney(p.price_min, theme.moneyFormat);
        /* the exact class the server-rendered recs cards use — same red styling */
        priceDiv.classList.add('grid-product__price--on-sale');
      }
      var mask = card.querySelector('.grid-product__image-mask');
      if (mask && !mask.querySelector('.grid-product__tag--sale')) {
        var pct = Math.round((p.compare_at_price_max - p.price_min) * 100 / p.compare_at_price_max);
        var tag = document.createElement('div');
        tag.className = 'grid-product__tag grid-product__tag--sale';
        tag.setAttribute('dir', 'ltr'); /* keeps "-10%" from flipping to "10%-" in RTL */
        tag.textContent = '-' + pct + '%';
        mask.appendChild(tag);
      }
    });
  }
  /* scan #10: 1-2 cards in a 5-slot 652px section read as a broken row on
     desktop — show recently-viewed only once there is real history (3+) */
  function orphanGuard(host) {
    if (!window.matchMedia('(min-width: 769px)').matches) return;
    var count = host.querySelectorAll('.grid-product').length;
    if (count > 0 && count < 3) host.style.display = 'none';
    else if (count >= 3) host.style.display = '';
  }

  function init() {
    whenTheme(function () {
      theme.settings.predictiveSearchPrice = true;
      document.querySelectorAll('[data-section-type="recently-viewed"]').forEach(function (host) {
        var grid = host.querySelector('.grid');
        if (!grid) return;
        try { new MutationObserver(function () { enrich(host); orphanGuard(host); }).observe(grid, { childList: true }); } catch (e) {}
        enrich(host);
        orphanGuard(host);
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
   __jdgmVerifiedPlural (Ido, 2026-07-03): the Judge.me SUMMARY chip says
   "מאומת" next to "8 ביקורות" — grammatically singular for a plural count.
   Rewrite to "מאומתות" (the per-review chip stays singular). The widget is
   Vue-rendered async → MutationObserver; our own textContent swap yields a
   no-op on re-entry (text already changed), so no loop.
   ============================================================ */
(function __jdgmVerifiedPlural() {
  /* Ido 2026-07-03 v2: merge the review COUNT into the black badge —
     "8 ביקורות מאומתות" as one pill (the ✔ icon is hidden via CSS), freeing
     header-row space. Idempotent: text only set when it differs. */
  function fix() {
    var badge = document.querySelector('.jm-verified-badge');
    if (!badge) return;
    var txt = badge.querySelector('.jm-text');
    if (!txt) return;
    var cluster = badge.parentElement;
    var countEl = cluster ? Array.prototype.find.call(cluster.children, function (el) {
      return el !== badge && /ביקורות/.test(el.textContent || '');
    }) : null;
    if (countEl) {
      var want = countEl.textContent.trim() + ' מאומתות';
      if (txt.textContent.trim() !== want) txt.textContent = want;
      countEl.style.display = 'none';
    } else if (txt.textContent.trim() === 'מאומת') {
      txt.textContent = 'מאומתות';
    }
    /* 2.4 (Ido GO): untranslated truncation links inside review cards */
    document.querySelectorAll('.jdgm-review-card__more-text').forEach(function (el) {
      var t = el.textContent.trim();
      if (t === 'More') el.textContent = 'עוד';
      else if (t === 'Less') el.textContent = 'פחות';
    });
    /* Ido: the average shows as "4.5" — append a quiet "/5" (out of five).
       Scoped to the badge's own column so histogram/filter numbers never match. */
    var col = badge.closest('.jm-stack') || badge.parentElement;
    var leafs = col ? col.querySelectorAll('*') : [];
    for (var i = 0; i < leafs.length; i++) {
      var el = leafs[i];
      if (el.children.length === 0 && !el.dataset.outof && /^[0-5](\.\d)?$/.test((el.textContent || '').trim())) {
        el.dataset.outof = '1';
        /* the number+slash fragment must be ONE LTR isolate or bidi renders "5/" */
        el.innerHTML = '<span class="pdp-avg-star">★</span><span dir="ltr">' + el.textContent.trim() + '<span class="pdp-outof">/5</span></span>';
        break;
      }
    }
  }
  function arm() {
    if (!document.body.classList.contains('template-product')) return;
    var host = document.getElementById('judgeme_product_reviews');
    if (host) { try { new MutationObserver(fix).observe(host, { childList: true, subtree: true }); } catch (e) {} }
    fix();
    setTimeout(fix, 2500);
    setTimeout(fix, 5000);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arm);
  else arm();
})();

/* ============================================================
   __qtyConfirm (Ido, 2026-07-03): while the qty number is being edited,
   show a red circular ✓ under the stepper (same confirm language as the
   colour-picker). Tapping it blurs the input — the native change event
   then runs the theme's own QtySelector update (theme.js:3106 binds
   change on .js-qty__num). Cart contexts only (drawer + /cart page).
   ============================================================ */
(function __qtyConfirm() {
  function makeBtn() {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'qty-confirm';
    b.setAttribute('aria-label', 'אישור כמות');
    b.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="4.5 13 10 18.5 19.5 6.5" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return b;
  }
  function removeBtn(btn) {
    if (!btn) return;
    btn.classList.remove('is-on');
    setTimeout(function () { if (btn.parentNode) btn.parentNode.removeChild(btn); }, 200);
  }
  document.addEventListener('focusin', function (e) {
    var el = e.target;
    if (!el || !el.classList || !el.classList.contains('js-qty__num')) return;
    if (!el.closest('#CartDrawer') && !el.closest('#CartPageForm')) return;
    var wrap = el.closest('.js-qty__wrapper');
    if (!wrap) return;
    /* host on the cart item — the wrapper's overflow:hidden clips children below it */
    var host = el.closest('.cart__item') || wrap.parentElement;
    var btn = host.querySelector('.qty-confirm');
    if (!btn) {
      btn = makeBtn();
      var wr = wrap.getBoundingClientRect();
      var hr = host.getBoundingClientRect();
      btn.style.left = (wr.left - hr.left + wr.width / 2) + 'px';
      btn.style.top = (wr.bottom - hr.top + 10) + 'px';
      host.appendChild(btn);
      btn.addEventListener('pointerdown', function (ev) {
        ev.preventDefault(); /* don't steal focus — we blur explicitly */
        el.blur();           /* fires native change if the value changed → theme updates the cart */
        removeBtn(btn);
      });
    }
    requestAnimationFrame(function () { btn.classList.add('is-on'); });
  });
  document.addEventListener('focusout', function (e) {
    var el = e.target;
    if (!el || !el.classList || !el.classList.contains('js-qty__num')) return;
    var host = el.closest('.cart__item') || document;
    setTimeout(function () { removeBtn(host.querySelector('.qty-confirm')); }, 150);
  });
})();

/* ============================================================
   __pdpGalleryArrows (Ido, 2026-07-04): prev/next arrows over the main
   product image on DESKTOP. The main photos are already a Flickity carousel
   (wrapAround, rightToLeft) even at >=769px — the theme just never renders
   buttons for it there. Flickity is a UMD global, so we reach the live
   instance with Flickity.data() at click time (variant switches destroy and
   recreate it on the same node — never cache the instance). RTL: "next" moves
   the strip leftward, so the next-arrow sits on the LEFT. Buttons are created
   on both viewports and hidden on mobile by CSS (survives rotation).
   ============================================================ */
(function __pdpGalleryArrows() {
  var CHEV_L = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CHEV_R = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function makeArrow(slideshow, isNext) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'pdp-gallery-arrow pdp-gallery-arrow--' + (isNext ? 'next' : 'prev');
    b.setAttribute('aria-label', isNext ? 'התמונה הבאה' : 'התמונה הקודמת');
    b.innerHTML = isNext ? CHEV_L : CHEV_R;
    b.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var f = window.Flickity && window.Flickity.data(slideshow);
      if (!f) return;
      if (isNext) { f.next(); } else { f.previous(); }
    });
    return b;
  }

  /* shared by the PDP (boot) and the quick-shop modal (on open) — idempotent,
     so a re-opened modal never double-appends (Ido 2026-07-07) */
  function attach(host) {
    if (!host || host.querySelector('.pdp-gallery-arrow')) return;
    var slideshow = host.querySelector('.product-slideshow');
    if (!slideshow) return;
    var imagesWrap = slideshow.closest('[data-product-images]');
    if (!imagesWrap || imagesWrap.getAttribute('data-has-slideshow') !== 'true') return;

    host.appendChild(makeArrow(slideshow, false));
    host.appendChild(makeArrow(slideshow, true));

    /* zoom affordance hint (scan #4): the theme's zoom button is stretched over
       the whole image at opacity 0 — customers get no clue zoom exists. This is
       a pure visual marker (pointer-events:none); clicks fall through to it. */
    if (slideshow.getAttribute('data-zoom') === 'true' && !host.querySelector('.pdp-zoom-hint')) {
      var hint = document.createElement('div');
      hint.className = 'pdp-zoom-hint';
      hint.setAttribute('aria-hidden', 'true');
      hint.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">'
        + '<circle cx="11" cy="11" r="6" stroke="currentColor" stroke-width="2"/>'
        + '<path d="M15.6 15.6L20 20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>'
        + '<path d="M11 8.8v4.4M8.8 11h4.4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>'
        + '</svg>';
      host.appendChild(hint);
    }
  }

  if (document.body.classList.contains('template-product')) {
    attach(document.querySelector('.page-content--product .product__main-photos'));
  }
  /* quick-shop modal: the gallery arrives via AJAX after the open click */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest || !e.target.closest('.quick-product__btn')) return;
    [450, 1500].forEach(function (ms) {
      setTimeout(function () {
        document.querySelectorAll('.modal--quick-shop.modal--is-active .product__main-photos').forEach(attach);
      }, ms);
    });
  }, true);
})();


/* ============================================================
   __a11yPill (Ido, 2026-07-05, rewrite): keep the Terminal X pill on the
   accessibility launcher. The widget has EXACTLY two launcher elements —
   the boot stub #Sea-Accessibility__LoaderButton and the full-app button
   #Sea-Accessibility__ButtonWidget, which replaces it a few seconds in.
   Their container #Sea-Accessibility__button holds the whole MENU too and
   must never be touched (a previous broad version hid every menu icon).
   Styling runs inside the MutationObserver callback = before first paint,
   so the widget's default look never reaches the screen.
   ============================================================ */
(function __a11yPill() {
  var PILL = 'url("data:image/svg+xml,%3Csvg%20width%3D%2731%27%20height%3D%2730%27%20viewBox%3D%270%200%2031%2030%27%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%3E%3Crect%20fill%3D%27%23000%27%20x%3D%270%27%20y%3D%270%27%20width%3D%2760%27%20height%3D%2730%27%20rx%3D%2715%27%2F%3E%3Crect%20stroke%3D%27%23fff%27%20stroke-width%3D%272%27%20fill%3D%27%23000%27%20x%3D%273%27%20y%3D%273%27%20width%3D%2754%27%20height%3D%2724%27%20rx%3D%2712%27%2F%3E%3Cg%20transform%3D%27translate(11.2%2C7)%20scale(0.667)%27%20fill%3D%27%23fff%27%3E%3Cpath%20d%3D%27M12%202c1.1%200%202%20.9%202%202s-.9%202-2%202-2-.9-2-2%20.9-2%202-2zM19%2013v-2c-1.54.02-3.09-.75-4.07-1.83l-1.29-1.43c-.17-.19-.38-.34-.61-.45-.24-.12-.5-.19-.79-.19h-.16c-1.05.11-1.81%201.04-1.81%202.09V15c0%201.1.9%202%202%202h5v5h2v-5.5c0-1.1-.9-2-2-2h-3v-3.45c1.29%201.07%203.25%201.94%205%201.95zm-6.17%205c-.41%201.16-1.52%202-2.83%202-1.66%200-3-1.34-3-3%200-1.31.84-2.41%202-2.83V12.1c-2.28.46-4%202.48-4%204.9%200%202.76%202.24%205%205%205%202.42%200%204.44-1.72%204.9-4h-2.07z%27%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E")';
  var RULES = {
    'left': 'auto', 'right': '0px', 'top': 'auto',
    'width': '31px', 'height': '30px',
    'border': '0px none', 'border-radius': '0px', 'padding': '0px',
    'margin': '0px', 'box-shadow': 'none',
    'background-color': 'transparent', 'background-image': PILL,
    'background-repeat': 'no-repeat', 'background-position': 'center center',
    'background-size': '31px 30px'
  };

  /* the PDP sticky buy bar (3.2) docks at the same corner — lift the pill
     just above it while the bar is shown */
  function expectedBottom() {
    var bar = document.querySelector('.pdp-stickybar.is-on');
    if (!bar) return '64px';
    var r = bar.getBoundingClientRect();
    /* lift only when the bar actually reaches the pill's corner — the
       desktop dock is centred and leaves the right edge free */
    if (r.right < window.innerWidth - 70) return '64px';
    var clear = Math.round(window.innerHeight - r.top) + 12;
    return (clear > 12 ? clear : 76) + 'px';
  }

  function style(el) {
    if (!el) return;
    var want = expectedBottom();
    if (el.style.getPropertyValue('background-image').indexOf('svg+xml') >= 0 &&
        el.style.getPropertyPriority('right') === 'important' &&
        el.style.getPropertyValue('bottom') === want) return;
    el.style.setProperty('bottom', want, 'important');
    for (var k in RULES) el.style.setProperty(k, RULES[k], 'important');
    for (var c = el.firstElementChild; c; c = c.nextElementSibling) {
      if (c.tagName === 'SVG' || c.tagName === 'svg' || c.tagName === 'IMG') {
        c.style.setProperty('display', 'none', 'important');
      }
    }
  }

  function tick() {
    style(document.getElementById('Sea-Accessibility__LoaderButton'));
    style(document.getElementById('Sea-Accessibility__ButtonWidget'));
  }

  /* observer runs before the next paint — pre-paint styling, and it is cheap
     (two getElementById calls) so no debounce is needed */
  new MutationObserver(tick).observe(document.documentElement, { childList: true, subtree: true });
  /* belt for late style rewrites by the widget */
  [500, 1500, 3000, 6000, 10000, 15000].forEach(function (t) { setTimeout(tick, t); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', tick);
  else tick();
  window.__pillTick = tick; /* the sticky bar calls this when it toggles */
})();

/* ============================================================
   __stickyAtc — phase 3.2 (Ido GO, 2026-07-05): sticky buy bar, BOTH
   viewports. Appears once the real add-to-cart scrolls above the viewport;
   its button opens the SAME colour-picker sheet the cart uses (his spec:
   "כמו שעשינו בתפריט") via window.__tikSheet, picking a colour also syncs
   the page's variant state, then adds qty 1 and the theme opens the drawer.
   Single-variant products add directly. All-sold-out renders disabled.
   ============================================================ */
(function __stickyAtc() {
  if (!document.body.classList.contains('template-product')) return;
  var form = document.querySelector('.product-single__form');
  var mainBtn = form && form.querySelector('.btn.add-to-cart');
  var varTa = document.querySelector('[data-variant-json]');
  if (!form || !mainBtn || !varTa) return;

  var variants = [];
  try { variants = JSON.parse(varTa.value); } catch (e) { return; } /* .value, not innerHTML — NBSP gotcha */
  if (!variants.length) return;

  function money(c) {
    /* the store's own formatter (thousands separator etc.) when available */
    try {
      if (window.theme && theme.Currency && theme.moneyFormat) {
        return theme.Currency.formatMoney(c, theme.moneyFormat).replace(/\.00(\s|$|₪)/, '$1');
      }
    } catch (e) {}
    var s = (c / 100).toFixed(2).replace(/\.00$/, '');
    return s.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' ₪';
  }
  function currentVariant() {
    var checked = document.querySelector('.variant-input input[type=radio]:checked');
    if (checked) {
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].option1 === checked.value) return variants[i];
      }
    }
    return variants[0];
  }
  var pageThumb = (function () {
    var img = document.querySelector('.product-main-slide img');
    return img ? (img.currentSrc || img.src) : '';
  })();

  var allSoldOut = variants.every(function (v) { return !v.available; });

  var bar = document.createElement('div');
  bar.className = 'pdp-stickybar';
  bar.innerHTML = '<div class="pdp-stickybar__inner">'
    + '<div class="pdp-stickybar__info">'
    + (pageThumb ? '<img class="pdp-stickybar__thumb" alt="" src="' + pageThumb + '">' : '')
    + '<div class="pdp-stickybar__meta">'
    + '<div class="pdp-stickybar__title"></div>'
    + '<div class="pdp-stickybar__price" data-sb-price dir="rtl"></div>'
    + '</div></div>'
    + '<button type="button" class="pdp-stickybar__btn" data-sb-btn>'
    + (allSoldOut ? 'אזל מהמלאי' : 'הוספה לעגלה') + '</button>'
    + '</div>';
  bar.querySelector('.pdp-stickybar__title').textContent = document.title.split('–')[0].split('|')[0].trim();
  var h1 = document.querySelector('h1.product-single__title');
  if (h1) bar.querySelector('.pdp-stickybar__title').textContent = h1.textContent.trim();
  document.body.appendChild(bar);
  var btn = bar.querySelector('[data-sb-btn]');
  if (allSoldOut) btn.disabled = true;

  function renderPrice(v) {
    var el = bar.querySelector('[data-sb-price]');
    if (!v) return;
    /* every amount is isolated dir=ltr — a bare Latin number next to the
       neutral ₪ inside an RTL row swaps the sign onto the wrong amount */
    if (v.compare_at_price && v.compare_at_price > v.price) {
      var pct = Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price);
      el.innerHTML = '<span class="sb-now"><b dir="ltr">' + money(v.price) + '</b>'
        + '<i dir="ltr">(-' + pct + '%)</i></span>'
        + '<s dir="ltr">' + money(v.compare_at_price) + '</s>';
      el.classList.add('on-sale');
    } else {
      el.innerHTML = '<span class="sb-now"><b dir="ltr">' + money(v.price) + '</b></span>';
      el.classList.remove('on-sale');
    }
  }
  renderPrice(currentVariant());
  document.addEventListener('variant:change', function (evt) {
    if (evt.detail && evt.detail.variant) renderPrice(evt.detail.variant);
  });

  /* visibility: only after the real button is scrolled ABOVE the viewport */
  var visible = false;
  function setVis(on) {
    if (on === visible) return;
    visible = on;
    bar.classList.toggle('is-on', on);
    if (window.__pillTick) window.__pillTick();
  }
  /* fresh query every check — the theme re-renders the buy area after init,
     so an IntersectionObserver pinned to the boot-time button node goes dead */
  var ticking = false;
  function check() {
    ticking = false;
    var btnNow = document.querySelector('.product-single__form .btn.add-to-cart');
    if (!btnNow) return;
    setVis(btnNow.getBoundingClientRect().bottom < 0);
  }
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(check);
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  check();

  function syncPageVariant(v) {
    var input = document.querySelector('.variant-input[data-value="' + (v.option1 || '').replace(/"/g, '\\"') + '"] input[type=radio]');
    if (input && !input.checked) input.click();
  }

  btn.addEventListener('click', function () {
    if (allSoldOut || !window.__tikSheet) return;
    var avail = variants.filter(function (v) { return v.available; });
    if (!avail.length) return;
    if (variants.length === 1) { window.__tikSheet.add(variants[0].id, btn); return; }
    var chips = avail.map(function (v) {
      var img = (v.featured_image && v.featured_image.src) || pageThumb;
      return { id: v.id, name: v.option1, img: img, current: false };
    });
    window.__tikSheet.open('בחירת צבע', chips, function (id, chipBtn) {
      var picked = variants.filter(function (v) { return v.id === parseInt(id, 10) || v.id === id; })[0];
      if (picked) syncPageVariant(picked);
      window.__tikSheet.add(id, chipBtn);
      /* the theme opens its drawer only on its OWN form-submit path — after
         our fetch-add, ask it explicitly (theme.js: 'cart:open' → drawer.open) */
      setTimeout(function () { document.dispatchEvent(new CustomEvent('cart:open')); }, 650);
    });
  });
})();

/* ============================================================
   __pdpDims — phase 3.3 (Ido GO, 2026-07-05): Antler-style dimensions strip
   near the buy zone. Dimension DATA lives only as free text inside the
   descriptions (metafields cleanup is end-of-project), in four observed
   formats: Hebrew key-value lines (גובה 75 ס"מ), a מידות W×H×D triple,
   standalone English lines (84 L / 3.45 KGS / 70.5X44X29CM) and mixed lines.
   Conservative: the strip renders only when 2+ dims parse. The strip is
   ADDITIVE — source lines are never hidden (Ido 2026-07-06: the description
   must stay complete; de-dup happens in the end-of-project metafields pass).
   ============================================================ */
(function __pdpDims() {
  if (!document.body.classList.contains('template-product')) return;
  var rte = document.querySelector('[data-product-blocks] .product-block > .rte:not(.product__policies)');
  if (!rte) return;

  var ORDER = ['גובה', 'רוחב', 'עומק', 'אורך', 'מידות', 'נפח', 'משקל'];
  var UNIT_MAP = { 'CM': 'ס"מ', 'L': 'ליטר', 'KG': 'ק"ג', 'KGS': 'ק"ג' };
  function normUnit(u) {
    u = (u || '').replace(/[״"']/g, '"').toUpperCase();
    if (UNIT_MAP[u]) return UNIT_MAP[u];
    u = (u || '');
    if (/ס.?מ/.test(u)) return 'ס"מ';
    if (/ליטר|^ל/.test(u)) return 'ליטר';
    if (/ק.?ג/.test(u)) return 'ק"ג';
    if (/אינץ/.test(u)) return "אינץ'";
    return u;
  }

  var dims = {}; /* key -> value string */

  function parseLine(text) {
    var found = [];
    var covered = 0;
    var t = text;

    /* W×H×D triple (with or without unit) */
    var triple = t.match(/(\d+(?:\.\d+)?)\s*[X×x]\s*(\d+(?:\.\d+)?)\s*[X×x]\s*(\d+(?:\.\d+)?)\s*(ס[״"']?מ|CM)?/);
    if (triple) {
      found.push(['מידות', triple[1] + '×' + triple[2] + '×' + triple[3] + ' ' + (normUnit(triple[4]) || 'ס"מ')]);
      covered += triple[0].length;
      t = t.replace(triple[0], ' ');
    }

    /* Hebrew key ... number unit */
    var re = /(גובה|רוחב|עומק|אורך|נפח|משקל)[^0-9]{0,24}?([\d.,]+)\s*(ס[״"']?מ|ליטר|ל[׳']?(?=\s|$)|ק[״"']?ג|אינץ[׳']?|CM|KGS?|L)/g;
    var m;
    while ((m = re.exec(t)) !== null) {
      found.push([m[1], m[2] + ' ' + normUnit(m[3])]);
      covered += m[0].length;
    }
    t = t.replace(re, ' ');

    /* standalone English value lines */
    var en = t.match(/^\s*([\d.]+)\s*(L|KGS?)\s*$/i);
    if (en) {
      found.push([/L/i.test(en[2]) && !/KG/i.test(en[2]) ? 'נפח' : 'משקל', en[1] + ' ' + normUnit(en[2])]);
      covered += en[0].length;
      t = t.replace(en[0], ' ');
    }

    /* leftover after removing matches + filler tokens (incl. inch
       parentheticals like "(28 אינצ')" riding along a cm value) */
    var leftover = t
      .replace(/\(?\s*\d+(?:\.\d+)?\s*אינ[צץ]['׳"״]?\s*\)?/g, '')
      .replace(/מידות|כולל גלגלים|כ-/g, '')
      .replace(/[:·,.()\s"״׳'-]/g, '')
      .trim();
    return { found: found, pure: found.length > 0 && leftover.length < 4 };
  }

  /* Ido 2026-07-06: the description is never censored — source spec lines
     STAY in the text (the strip is additive; the metafields cleanup will
     de-duplicate at the source, end-of-project). */
  [].slice.call(rte.querySelectorAll('p, li')).forEach(function (el) {
    var lines = (el.innerText || '').split('\n').map(function (s) { return s.trim(); }).filter(Boolean);
    lines.forEach(function (line) {
      parseLine(line).found.forEach(function (kv) { if (!dims[kv[0]]) dims[kv[0]] = kv[1]; });
    });
  });

  var keys = ORDER.filter(function (k) { return dims[k]; });
  if (keys.length < 2) return;

  var strip = document.createElement('div');
  strip.className = 'pdp-dims';
  strip.innerHTML = keys.map(function (k) {
    return '<div class="pdp-dims__cell"><span class="pdp-dims__label">' + k + '</span>'
      + '<span class="pdp-dims__value" dir="ltr">' + dims[k] + '</span></div>';
  }).join('');

  var descBlock = rte.closest('.product-block');
  if (!descBlock) return;
  descBlock.insertAdjacentElement('afterend', strip);
})();

/* ============================================================
   __compIndicator (Ido, 2026-07-05): the complementary carousel showed two
   page DOTS — replaced with the same slim right-left progress bar used by
   the colour strip and the colour sheet. Syncs to flickity's scroll
   progress (instance via the UMD global, fetched after the AJAX load).
   ============================================================ */
(function __compIndicator() {
  if (!document.body.classList.contains('template-product')) return;

  function init() {
    var comp = document.querySelector('product-recommendations[data-intent="complementary"]');
    if (!comp) return false;
    var carousel = comp.querySelector('.product-single__related.flickity-enabled');
    if (!carousel || !window.Flickity) return false;
    var fl = window.Flickity.data(carousel);
    if (!fl || comp.querySelector('.cart-vpick__ind')) return !!comp.querySelector('.cart-vpick__ind');

    /* flickity measured its cells before the polish CSS (margin/width fixes)
       landed, leaving every slide +34px off in RTL — remeasure with the
       final layout so the cards sit inside the container again */
    fl.resize();

    var ind = document.createElement('div');
    ind.className = 'cart-vpick__ind';
    ind.innerHTML = '<div class="cart-vpick__ind-thumb"></div>';
    carousel.insertAdjacentElement('afterend', ind);
    var thumb = ind.firstChild;
    var slides = Math.max(1, (fl.slides || []).length);
    if (slides < 2) { ind.style.display = 'none'; return true; }
    thumb.style.width = Math.max(14, Math.round(100 / slides)) + '%';

    function place(progress) {
      var maxLeft = ind.clientWidth - thumb.offsetWidth;
      var pr = Math.min(1, Math.max(0, progress));
      thumb.style.insetInlineStart = Math.round(pr * maxLeft) + 'px';
    }
    fl.on('scroll', function (progress) { place(progress); });
    fl.on('select', function () { place(fl.selectedIndex / (slides - 1)); });
    place(0);
    return true;
  }

  /* the block loads via AJAX and flickity initializes after — retry politely */
  var tries = 0;
  var timer = setInterval(function () {
    tries++;
    if (init() || tries > 20) clearInterval(timer);
  }, 500);
  document.addEventListener('recommendations:loaded', function () { setTimeout(init, 350); });
})();

/* ============================================================
   __filterQueue (Ido GO 2026-07-07, collection review bug א2): theme.js
   gates filter renders behind an isAnimating lock — a checkbox change that
   lands while a render is in flight is preventDefault'ed and silently
   DROPPED (onFormSubmit ~6678), then the stale response re-renders the
   sidebar and visually reverts the user's click. Lost update, reproduced
   even at 1200ms spacing on slow responses. No .catch either — one failed
   request locks filtering until reload.
   Fix without touching theme.js: remember intents that arrive while a
   render is pending and replay them (one synthetic change carries the FULL
   form state) after collection:reloaded; a 9s watchdog hard-recovers from
   a never-resolving render by navigating with the intended params.
   ============================================================ */
(function __filterQueue() {
  var pending = false, queue = [], watchdog = null;

  function armWatchdog(formEl) {
    clearTimeout(watchdog);
    watchdog = setTimeout(function () {
      /* render never finished (dropped promise / network error): navigate
         with the full intended state — deterministic recovery */
      try {
        var params = new URLSearchParams(new FormData(formEl));
        window.location.assign(window.location.pathname + '?' + params.toString());
      } catch (e) {}
    }, 9000);
  }

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.closest || !t.closest('.filter-form')) return;
    if (!t.matches('input') || !/checkbox|radio/i.test(t.type || '')) return;
    if (!pending) {
      pending = true;                       /* the theme takes this one */
      armWatchdog(t.closest('.filter-form'));
      return;
    }
    /* the theme will silently drop this event — keep the intent */
    queue.push({ name: t.name, value: t.value, checked: t.checked });
  }, true);

  document.addEventListener('collection:reloaded', function () {
    pending = false;
    clearTimeout(watchdog);
    if (!queue.length) return;
    /* macrotask delay: theme.js dispatches collection:reloaded BEFORE it
       clears its isAnimating lock (6769→6771) — a synchronous replay here is
       swallowed by the same gate that dropped the original click */
    setTimeout(function () {
      var form = document.querySelector('.filter-form');
      var q = queue.splice(0);
      if (!form) return;
      var last = null;
      q.forEach(function (it) {
        var input = form.querySelector('input[name="' + CSS.escape(it.name) + '"][value="' + CSS.escape(it.value) + '"]');
        if (input) { input.checked = it.checked; last = input; }
      });
      if (last) {
        /* one replayed event on a real input (theme reads closest('li'))
           re-renders with the FULL form state — all queued intents at once.
           MUST be 'input': theme.js binds form.addEventListener('input', ...)
           (6617) — a 'change' replay is invisible to it and recovery would
           fall back to the slow watchdog full-reload. */
        pending = true;
        armWatchdog(form);
        last.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }, 60);
  });
})();

/* ============================================================
   __popoverGuard (Ido GO 2026-07-07, collection review bug א3): the theme's
   sort popover calls showPopover() on an element that an AJAX re-render
   already disconnected → InvalidStateError pageerror on every collection
   load. Guard the API; anything still connected behaves exactly as before.
   ============================================================ */
(function __popoverGuard() {
  if (!window.HTMLElement || !HTMLElement.prototype.showPopover) return;
  var orig = HTMLElement.prototype.showPopover;
  HTMLElement.prototype.showPopover = function () {
    if (!this.isConnected) return;
    try { return orig.apply(this, arguments); }
    catch (e) { if (!e || e.name !== 'InvalidStateError') throw e; }
  };
})();

/* ============================================================
   __collectionReturnBack (Ido 2026-07-08): the PDP's "חזרה אל {קולקציה}"
   button navigates fresh, which always lands at the grid top. When the
   shopper actually CAME from that collection (same-origin referrer, same
   pathname), turn the click into a real history.back() so the existing
   scroll-restore returns them to the exact spot — filters included. Direct
   entries (Google, home, another collection) keep the plain link.
   ============================================================ */
(function __collectionReturnBack() {
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a.return-link');
    if (!a) return;
    try {
      if (window.history.length < 2 || !document.referrer) return;
      var ref = new URL(document.referrer);
      var target = new URL(a.getAttribute('href'), window.location.origin);
      if (ref.origin !== window.location.origin) return;
      if (decodeURIComponent(ref.pathname) !== decodeURIComponent(target.pathname)) return;
      e.preventDefault();
      window.history.back();
    } catch (err) { /* fall through to the plain link */ }
  }, true);
})();

/* ============================================================
   __filterScrollTop (Ido 2026-07-08): applying a filter re-renders the grid
   via AJAX but the viewport stayed mid-page — bring the results' top back
   into view. Armed ONLY by a real user filter/sort touch (inputs named
   filter.* / sort_by within the last 6s), so pagination clicks and the
   Back-from-PDP restore are never affected.
   ============================================================ */
(function __filterScrollTop() {
  if (!document.querySelector('[data-scroll-to]')) return;
  var lastFilterTouch = 0;
  document.addEventListener('input', function (e) {
    var n = (e.target && e.target.name) || '';
    if (n.indexOf('filter.') === 0 || n === 'sort_by') lastFilterTouch = Date.now();
  }, true);
  document.addEventListener('change', function (e) {
    var n = (e.target && e.target.name) || '';
    if (n === 'sort_by') lastFilterTouch = Date.now();
  }, true);
  document.addEventListener('collection:reloaded', function () {
    if (Date.now() - lastFilterTouch > 6000) return;
    var el = document.querySelector('[data-scroll-to]');
    if (!el) return;
    var y = Math.max(0, el.getBoundingClientRect().top + window.pageYOffset - 90);
    if (window.pageYOffset > y + 4) window.scrollTo({ top: y, behavior: 'smooth' });
  });
})();

/* ============================================================
   __cleanStockParam (Ido 2026-07-07): "יש דרך שלא יראו אותו ב-URL?" — yes.
   The in-stock default stays enforced at the REQUEST level while the address
   bar stays clean:
   1. fetch patch: every collection section-render request that lacks
      filter.v.availability gets it injected (covers filters, sort,
      pagination, and Back/Forward re-renders whose history entries are
      already clean).
   2. history patch: any pushState/replaceState of a /collections/ URL drops
      the param before it ever reaches the address bar.
   First server render is covered by the redirect + pre-paint scrub in
   main-collection.liquid. The filter-form hidden inputs still submit the
   value, so the theme's own param merging stays coherent.
   ============================================================ */
(function __cleanStockParam() {
  var PARAM = 'filter.v.availability';

  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url);
      if (url && url.indexOf('section_id=') !== -1 && url.indexOf(PARAM) === -1) {
        var u = new URL(url, window.location.origin);
        // 2026-08-17: /search enforces the same in-stock default (see main-search.liquid).
        if (u.pathname.indexOf('/collections/') === 0 || u.pathname.indexOf('/search') === 0) {
          u.searchParams.set(PARAM, '1');
          input = (typeof input === 'string') ? u.toString() : new Request(u.toString(), input);
        }
      }
    } catch (e) {}
    return origFetch.call(this, input, init);
  };

  ['pushState', 'replaceState'].forEach(function (fn) {
    var orig = history[fn];
    history[fn] = function (state, title, url) {
      try {
        if (url) {
          var u = new URL(url, window.location.origin);
          if ((u.pathname.indexOf('/collections/') === 0 || u.pathname.indexOf('/search') === 0) && u.searchParams.has(PARAM)) {
            u.searchParams.delete(PARAM);
            url = u.pathname + (u.searchParams.toString() ? '?' + u.searchParams.toString() : '') + u.hash;
          }
        }
      } catch (e) {}
      return orig.call(this, state, title, url);
    };
  });
})();

/* ============================================================
   __storesChips (Ido GO 2026-07-07, stores phase A): sticky region-filter
   chips on the branches index. Fully self-maintaining: the bar is built from
   whatever headed text-columns sections exist on the page — add a region
   section in the editor and its chip appears; rename it and the chip follows.
   Gated to the /pages/our-stores path.
   ============================================================ */
(function __storesChips() {
  if (window.location.pathname.indexOf('/pages/our-stores') === -1) return;
  function init() {
    var regions = [].filter.call(document.querySelectorAll('.shopify-section'), function (s) {
      return s.querySelector('.text-column__grid') && s.querySelector('.section-header h2');
    });
    if (regions.length < 2 || document.querySelector('.stores-chips')) return;
    var bar = document.createElement('nav');
    bar.className = 'stores-chips';
    bar.setAttribute('aria-label', 'סינון לפי אזור');
    var row = document.createElement('div');
    row.className = 'stores-chips__row';
    bar.appendChild(row);
    var spacer = document.createElement('div');
    spacer.className = 'stores-chips-spacer';
    spacer.style.display = 'none';
    function mk(label, target) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'chip';
      b.textContent = label;
      b.addEventListener('click', function () {
        bar.querySelectorAll('.chip').forEach(function (c) { c.classList.remove('is-on'); });
        b.classList.add('is-on');
        regions.forEach(function (r) { r.style.display = (!target || r === target) ? '' : 'none'; });
        /* Ido 2026-07-08: every region pick returns to the top of the page */
        window.scrollTo({ top: 0, behavior: 'smooth' });
        b.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      });
      return b;
    }
    var all = mk('הכל', null);
    all.classList.add('is-on');
    row.appendChild(all);
    regions.forEach(function (r) {
      row.appendChild(mk(r.querySelector('.section-header h2').textContent.trim(), r));
    });
    /* slim progress bar (same .cart-vpick__ind pattern as the variant strips)
       so it's obvious the chip row scrolls sideways; hides itself when the
       chips fit (desktop) */
    var ind = document.createElement('div');
    ind.className = 'cart-vpick__ind';
    ind.innerHTML = '<div class="cart-vpick__ind-thumb"></div>';
    bar.appendChild(ind);
    var thumb = ind.firstChild;
    function syncInd() {
      var over = row.scrollWidth - row.clientWidth;
      if (over < 8) { ind.style.display = 'none'; return; }
      ind.style.display = '';
      thumb.style.width = Math.max(14, Math.round(row.clientWidth / row.scrollWidth * 100)) + '%';
      var maxLeft = ind.clientWidth - thumb.offsetWidth;
      var pr = Math.min(1, Math.abs(row.scrollLeft) / over); /* RTL: scrollLeft negative */
      thumb.style.insetInlineStart = Math.round(pr * maxLeft) + 'px';
    }
    row.addEventListener('scroll', syncInd, { passive: true });
    window.addEventListener('resize', syncInd);
    regions[0].parentNode.insertBefore(spacer, regions[0]);
    regions[0].parentNode.insertBefore(bar, regions[0]);
    syncInd();
    setTimeout(syncInd, 400);
    /* the theme's fixed header (.site-header--stuck, 65px mobile / full nav
       desktop) sat ON TOP of the stuck bar — keep the bar's sticky offset
       glued to the header's live bottom edge (follows the slide animation) */
    var lastOff = -1;
    function syncTop() {
      var h = document.querySelector('.site-header--stuck');
      var off = 0;
      if (h) {
        /* desktop keeps the stuck header at top:37 (toolbar strip above it) —
           only the visible bottom edge matters */
        off = Math.max(0, Math.round(h.getBoundingClientRect().bottom));
      }
      if (off !== lastOff) {
        lastOff = off;
        document.documentElement.style.setProperty('--tt-chips-top', off + 'px');
      }
    }
    /* sticky insurance (Ido 2026-07-08): iOS Safari sometimes neuters
       position:sticky — if the bar's top ever crosses above its offset,
       pin it with position:fixed and hold the layout with the spacer.
       When sticky works (top clamps to the offset) this never engages. */
    var pinned = false;
    function onScroll() {
      syncTop();
      if (!pinned) {
        if (bar.getBoundingClientRect().top < lastOff - 1) {
          pinned = true;
          spacer.style.height = bar.offsetHeight + 'px';
          spacer.style.display = '';
          bar.classList.add('is-pinned');
        }
      } else if (spacer.getBoundingClientRect().top >= lastOff) {
        pinned = false;
        spacer.style.display = 'none';
        bar.classList.remove('is-pinned');
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    /* the header keeps sliding for ~0.5s after the scroll stops — re-measure
       a few times until it settles */
    var settle = [];
    window.addEventListener('scroll', function () {
      settle.forEach(clearTimeout);
      settle = [150, 400, 800].map(function (ms) { return setTimeout(syncTop, ms); });
    }, { passive: true });
    syncTop();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
   __waFab (Ido 2026-07-08): floating WhatsApp chat button, Terminal-X style —
   60px black circle, bottom-left (bottom-right is the accessibility widget's),
   site-wide. Chat only — the number itself stays unpublished by design.
   ============================================================ */
(function __waFab() {
  function add() {
    if (document.querySelector('.wa-fab')) return;
    var a = document.createElement('a');
    a.className = 'wa-fab';
    a.href = 'https://wa.me/97239037774';
    a.target = '_blank';
    a.rel = 'noopener';
    a.setAttribute('aria-label', 'צ\'אט WhatsApp עם שירות הלקוחות');
    a.innerHTML = '<svg viewBox="0 0 24 24" width="31" height="31" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>';
    document.body.appendChild(a);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', add);
  else add();
})();

/* ============================================================
   __telGuard (Ido 2026-07-08): iOS pops "This website has been blocked from
   automatically starting a call" when a tel: link fires twice in quick
   succession (a nervous double-tap) — and then keeps blocking normal taps.
   Swallow the repeats: one tel: activation per 1.5s, site-wide.
   ============================================================ */
(function __telGuard() {
  var last = 0;
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href^="tel:"]');
    if (!a) return;
    var now = Date.now();
    if (now - last < 1500) { e.preventDefault(); e.stopPropagation(); return; }
    last = now;
  }, true);
})();

/* ============================================================
   __storesReturn (Ido 2026-07-07): "חזרה לכל הסניפים" lands exactly where the
   user left the index. Branch side: the back link becomes history.back() when
   the user actually came from the index (bfcache then restores scroll + chip
   state natively). Index side: scroll position + active region chip are saved
   on card-link click and re-applied on a fresh (non-bfcache) return.
   ============================================================ */
(function __storesReturn() {
  var KEY = 'tt-stores-return';
  var back = document.querySelector('.branch-info__back');
  if (back) {
    back.addEventListener('click', function (e) {
      try {
        if (document.referrer.indexOf('/pages/our-stores') !== -1 && window.history.length > 1) {
          e.preventDefault();
          window.history.back();
        }
      } catch (err) { /* fall through to the plain href */ }
    });
    return;
  }
  if (window.location.pathname.indexOf('/pages/our-stores') === -1) return;
  // save state when leaving to a branch page via a card link
  document.addEventListener('click', function (e) {
    var a = e.target.closest ? e.target.closest('.text-column__grid a[href*="/pages/"]') : null;
    if (!a) return;
    try {
      var on = document.querySelector('.stores-chips .chip.is-on');
      sessionStorage.setItem(KEY, JSON.stringify({ y: window.scrollY, chip: on ? on.textContent : '', t: Date.now() }));
    } catch (err) {}
  }, true);
  // fresh-load return (no bfcache): re-apply chip filter, then scroll
  var nav = window.performance && performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
  var isBack = nav && nav.type === 'back_forward';
  var fromBranch = /\/pages\/(store-|big-fashion|%D7)/.test(document.referrer);
  if (!isBack && !fromBranch) return;
  var st = null;
  try { st = JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (err) {}
  if (!st || Date.now() - st.t > 1800000) return;
  function apply() {
    if (st.chip && st.chip !== 'הכל') {
      var chips = document.querySelectorAll('.stores-chips .chip');
      for (var i = 0; i < chips.length; i++) {
        if (chips[i].textContent === st.chip && !chips[i].classList.contains('is-on')) { chips[i].click(); break; }
      }
    }
    window.scrollTo(0, st.y);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { apply(); setTimeout(apply, 300); });
  else { apply(); setTimeout(apply, 300); }
})();

/* ============================================================
   __ttTypeahead v2 (Ido 2026-07-10, "בלי אפליקציה חיצונית" → "תשפר הכל"):
   native search typeahead. Shopify's predictive-search API 417s on
   Hebrew-locale stores, so we fetch the REGULAR /search route through the
   Section Rendering API (sections/search-typeahead.liquid) and drive the
   theme's existing <predictive-search> drawer UI. The theme's own fetch is
   neutralized (dead route; racing it would wipe our paint on slow 417s).
   v2 adds: loading state, keyboard nav + aria, sessionStorage query cache,
   recent+popular panel on empty focus, match highlighting, category
   suggestion row, dataLayer events (zero-results / suggestion click).
   v3 (Ido "חקירת מומחה" 2026-07-11, IMG_1762): the suggestion panel became
   evidence-based — POPULAR is the store's real top searches (Shopify
   searches dataset, 90d, variants merged, results-verified); recents are
   quality-gated (results-gate + SKU-shape filter + Hebrew-normalized prefix
   dedup + 30d TTL + clear button + /search-landing validation for
   Enter-beats-debounce); chips are DOM-built (attr-safe), dir="auto",
   listbox/option roles; tt_search dataLayer event on every gated commit.
   Kill switch: window.__ttTypeaheadOff = true (set before this runs).
   ============================================================ */
(function () {
  if (window.__ttTypeaheadBound || window.__ttTypeaheadOff) return;
  window.__ttTypeaheadBound = true;

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 250;
  var SECTION_ID = 'search-typeahead';
  /* POPULAR (Ido "חקירת מומחה" 2026-07-11): REAL top store searches, not an
     editorial guess. Source: Admin ▸ Analytics ▸ reports, ShopifyQL
       FROM searches SHOW searches GROUP BY search_query SINCE -90d
     (1,670 searches; spelling/word variants merged: samsonite+סמסונייט=197,
     פאוץ+פאוץ'+פאוץ לנשים+לגבר≈130, bond+בונד=87, מזוודה+מזוודות+28"≈183).
     Every term verified live to return products (21–595 results, 2026-07-11).
     REFRESH: rerun that query, merge variants, keep terms with results,
     order by volume. Rendered minus whatever is already in the user's
     recents (normKey dedup at render time). */
  var POPULAR = ['מזוודה', 'סמסונייט', 'טרולי', 'תיק גב', 'בונד', 'פאוץ', 'תיק למחשב', 'תיק צד'];
  /* keyword -> category links. Single-word keys match a whole query word
     (or prefix pairing for keys of 4+ chars); spaced keys match by
     inclusion. First hit wins, max 2 links rendered. */
  var CATS = [
    { k: ['מזווד', 'מזוודות', 'טרולי'], links: [['מזוודות', '/collections/מזוודות']] },
    { k: ['למטוס', 'עלייה למטוס'], links: [['מזוודות עלייה למטוס', '/collections/מזוודות-עלייה-למטוס']] },
    { k: ['מחשב', 'לפטופ', 'למחשב'], links: [['תיקי גב למחשב', '/collections/תיקי-גב-למחשב']] },
    { k: ['ארנק', 'ארנקים'], links: [['ארנקים לנשים', '/collections/ארנקי-נשים'], ['ארנקי גברים', '/collections/ארנקי-גברים']] },
    { k: ['תיק גב', 'ילקוט', 'גב'], links: [['תיקי גב לנשים', '/collections/תיקי-גב-לנשים'], ['תיקי גב לגבר', '/collections/תיקי-גב-לגבר']] },
    { k: ['תיק צד', 'צד'], links: [['תיקי צד לנשים', '/collections/תיקי-צד-לנשים'], ['תיקי צד לגבר', '/collections/תיקי-צד-לגבר']] },
    { k: ['עסקים'], links: [['תיקי עסקים', '/collections/תיקי-עסקים']] },
    { k: ['סט'], links: [['סט מזוודות', '/collections/סט-מזוודות']] }
  ];

  /* "אין לנו" (Ido 2026-07-11): brands shoppers actually search but the
     store doesn't carry — sourced from the REAL no-results report (90d,
     ShopifyQL FROM searches WHERE search_results_were_returned = false).
     Zero-results dead end → honest answer + similar alternatives.
     SELF-HEALING: only consulted when the live search returns 0, so if a
     brand gets stocked later the message never shows. Numbers = 90d
     no-results searches at build time. */
  var NOT_CARRIED = [
    { k: ['kipling', 'קיפלינג'], /* 23 */ sugg: [['תיקי צד לנשים', '/collections/תיקי-צד-לנשים'], ['תיקי גב לנשים', '/collections/תיקי-גב-לנשים'], ['ארנקי נשים', '/collections/ארנקי-נשים']] },
    { k: ['lee cooper', 'lee coper', 'לי קופר', 'ליקופר'], /* 20 */ sugg: [['תיקי גב לגבר', '/collections/תיקי-גב-לגבר'], ['מזוודות', '/collections/מזוודות'], ['תיקי צד לגבר', '/collections/תיקי-צד-לגבר']] },
    { k: ['polo', 'marco polo', 'פולו', 'מרקו פולו'], /* 12 */ sugg: [['תיקי צד לגבר', '/collections/תיקי-צד-לגבר'], ['ארנקי גברים', '/collections/ארנקי-גברים'], ['תיקי גב לגבר', '/collections/תיקי-גב-לגבר']] },
    { k: ['thule', 'טולה', 'תולה'], /* 9 */ sugg: [['תיקי גב למחשב', '/collections/תיקי-גב-למחשב'], ['תיקי גב לגבר', '/collections/תיקי-גב-לגבר']] },
    { k: ['yael keidar', 'יעל קידר'], /* 9 */ sugg: [['תיקי צד לנשים', '/collections/תיקי-צד-לנשים'], ['תיקי גב לנשים', '/collections/תיקי-גב-לנשים']] },
    { k: ['מצלמה', 'למצלמה', 'תיק מצלמה'], /* 8 */ sugg: [['תיקים למחשב', '/collections/תיקי-גב-למחשב'], ['תיקי צד לגבר', '/collections/תיקי-צד-לגבר']] },
    { k: ['guess', 'גס'], /* 7 */ sugg: [['תיקי צד לנשים', '/collections/תיקי-צד-לנשים'], ['ארנקי נשים', '/collections/ארנקי-נשים']] },
    { k: ['hedgren', 'הדגרן'], /* 7 */ sugg: [['תיקי צד לנשים', '/collections/תיקי-צד-לנשים'], ['תיקי גב לנשים', '/collections/תיקי-גב-לנשים']] },
    { k: ['sprayground', 'ספרייגראונד', 'ספרייגרונד'], /* 4 */ sugg: [['תיקי גב לגבר', '/collections/תיקי-גב-לגבר'], ['תיקי גב לנשים', '/collections/תיקי-גב-לנשים']] },
    { k: ['osprey', 'אוספרי'], /* 4 */ sugg: [['תיקי גב לגבר', '/collections/תיקי-גב-לגבר'], ['מזוודות', '/collections/מזוודות']] },
    { k: ['rollink', 'רולינק'], /* 4 */ sugg: [['מזוודות', '/collections/מזוודות'], ['מזוודות עלייה למטוס', '/collections/מזוודות-עלייה-למטוס']] },
    { k: ['tumi', 'טומי'], /* 3 */ sugg: [['מזוודות', '/collections/מזוודות'], ['תיקי עסקים', '/collections/תיקי-עסקים']] },
    { k: ['jeep', 'גיפ', "ג'יפ"], /* 3 */ sugg: [['תיקי גב לגבר', '/collections/תיקי-גב-לגבר'], ['מזוודות', '/collections/מזוודות']] },
    { k: ['adidas', 'אדידס', 'nike', 'נייק'], /* 3+ */ sugg: [['תיקי גב לגבר', '/collections/תיקי-גב-לגבר'], ['תיקי גב לנשים', '/collections/תיקי-גב-לנשים']] }
  ];
  /* people type branch cities into product search (רמת גן 3, ראשון לציון 3…) */
  var STORES_WORDS = ['סניף', 'סניפים', 'חנות', 'חנויות', 'אילת', 'תל אביב', 'רמת גן', 'ראשון לציון', 'ירושלים', 'חיפה', 'באר שבע', 'אשדוד', 'נתניה', 'כפר סבא', 'פתח תקווה', 'גלילות', 'רמת השרון', 'דיזנגוף'];

  function wordHit(q, keys) {
    var nk = normKey(q);
    if (!nk || nk.length > 30) return false;
    var words = nk.split(' ');
    return keys.some(function (key) {
      var kk = normKey(key);
      if (kk.indexOf(' ') !== -1) return nk === kk || nk.indexOf(kk) !== -1;
      return words.indexOf(kk) !== -1;
    });
  }
  function notCarriedHit(q) {
    for (var i = 0; i < NOT_CARRIED.length; i++) {
      if (wordHit(q, NOT_CARRIED[i].k)) return NOT_CARRIED[i];
    }
    return null;
  }
  function zeroStateEl(q, nc, isStores) {
    var frag = document.createDocumentFragment();
    var msg = document.createElement('p');
    msg.className = 'tt-ta__none';
    if (isStores) msg.textContent = 'מחפשים סניף? כל הסניפים והשעות במקום אחד:';
    else {
      msg.dir = 'auto';
      msg.textContent = 'אין לנו "' + q + '" בחנות כרגע — אולי יתאימו לכם:';
    }
    frag.appendChild(msg);
    var row = document.createElement('div');
    row.className = 'tt-ta__chips';
    row.style.justifyContent = 'center';
    (isStores ? [['לכל הסניפים ›', '/pages/our-stores']] : nc.sugg).forEach(function (c) {
      var a = document.createElement('a');
      a.className = 'tt-ta__chip';
      a.href = c[1];
      a.dir = 'auto';
      var t = document.createElement('span');
      t.className = 'tt-ta__chip-txt';
      t.textContent = c[0];
      a.appendChild(t);
      row.appendChild(a);
    });
    frag.appendChild(row);
    return frag;
  }

  function esc(s) { var d = document.createElement('div'); d.textContent = s; return d.innerHTML; }
  /* esc() alone is NOT attribute-safe (audit 2026-07-11): it leaves double
     quotes, so a legit query like מזוודה 28" broke out of data-q="...".
     Use attrEsc for every string-built attribute; DOM-built chips are immune. */
  function attrEsc(s) { return esc(s).replace(/"/g, '&quot;'); }
  function rxEsc(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
  function cacheGet(q) { try { return sessionStorage.getItem('ttq:' + q); } catch (e) { return null; } }
  function cacheSet(q, html) {
    try {
      /* evict only OUR keys — counting foreign sessionStorage keys used to
         nuke the whole ttq: cache and blind the Enter-time results gate */
      var mine = Object.keys(sessionStorage).filter(function (k) { return k.indexOf('ttq:') === 0; });
      if (mine.length > 40) mine.slice(0, 20).forEach(function (k) { sessionStorage.removeItem(k); });
      sessionStorage.setItem('ttq:' + q, html);
    } catch (e) {}
  }

  /* ---------- recents v2 (expert audit 2026-07-11) ----------
     One gated commit path replaces the old save-anything addRecent that
     produced Ido's screenshot (SKU chip "201615", typo "סמסוניי" beside its
     correction "סמסונייט"). Junk cannot enter: shape filter (pure digit/
     punctuation strings out), results gate (known-zero queries out, unknowns
     validated on the /search landing), Hebrew-normalized prefix dedup
     (the typo dies the moment its correction commits). Storage: 10 kept /
     5 rendered, 30-day TTL, one-time migration cleans existing users. */
  var REC_KEY = 'tt-ta-recent2';
  function normKey(s) {
    return String(s || '').toLowerCase()
      .replace(/[׳‘’'"״“”`]/g, '')
      .replace(/ם/g, 'מ').replace(/ן/g, 'נ').replace(/ץ/g, 'צ').replace(/ף/g, 'פ').replace(/ך/g, 'כ')
      .replace(/וו/g, 'ו').replace(/יי/g, 'י')
      .replace(/\s+/g, ' ').trim();
  }
  function recInsert(arr, rawQ, ts) {
    var q = String(rawQ || '').replace(/\s+/g, ' ').trim();
    if (q.length < MIN_CHARS || q.length > 60) return arr;
    if (/^[\d\s.\-"'׳״]+$/.test(q)) return arr; /* SKU/barcode/phone shapes */
    var k = normKey(q);
    if (!k) return arr;
    var keepQ = q, keepK = k;
    var out = arr.filter(function (e) {
      if (e.k === keepK) return false;
      /* prefix subsumption (≥4 chars): typo/fragment merges into the fuller
         committed form — and re-committing the fragment bumps the full form */
      if (keepK.length >= 4 && e.k.length >= 4 && (e.k.indexOf(keepK) === 0 || keepK.indexOf(e.k) === 0)) {
        if (e.q.length > keepQ.length) { keepQ = e.q; keepK = e.k; }
        return false;
      }
      return true;
    });
    out.unshift({ q: keepQ, k: keepK, ts: ts });
    return out.slice(0, 10);
  }
  function saveRecents(arr) {
    try { localStorage.setItem(REC_KEY, JSON.stringify(arr)); }
    catch (e) {
      /* quota: sacrifice the rebuildable instant index once and retry */
      try { localStorage.removeItem('tt-idx-v3'); localStorage.setItem(REC_KEY, JSON.stringify(arr)); } catch (e2) {}
    }
  }
  function recents() {
    try {
      var now = Date.now();
      var arr = JSON.parse(localStorage.getItem(REC_KEY) || 'null');
      if (!arr) {
        /* one-time migration: legacy raw strings run through the same
           filters — retroactively deletes the junk existing users carry */
        arr = [];
        JSON.parse(localStorage.getItem('tt-ta-recent') || '[]').reverse().forEach(function (q) {
          arr = recInsert(arr, q, now);
        });
        localStorage.removeItem('tt-ta-recent');
        saveRecents(arr);
      }
      var cut = now - 30 * 864e5;
      return arr.filter(function (e) { return e && e.q && e.k && e.ts > cut; });
    } catch (e) { return []; }
  }
  function clearRecents() {
    try { localStorage.removeItem(REC_KEY); localStorage.removeItem('tt-ta-recent'); } catch (e) {}
  }
  function resolveCount(q) {
    var html = cacheGet(q);
    if (html) {
      var m = html.match(/data-results-count="(\d+)"/);
      if (m) return parseInt(m[1], 10);
    }
    try {
      if (window.__ttInstant && window.__ttInstant.ready()) {
        var r = window.__ttInstant.query(q);
        /* positive counts only — the instant index lacks S&D synonyms, so
           a local zero is inconclusive */
        if (r && r.html) return 1;
      }
    } catch (e) {}
    return null;
  }
  function commitRecent(rawQ, knownCount) {
    var q = String(rawQ || '').replace(/\s+/g, ' ').trim();
    if (q.length < MIN_CHARS) return;
    var count = typeof knownCount === 'number' && !isNaN(knownCount) ? knownCount : resolveCount(q);
    if (count === 0) return; /* known-zero never becomes a chip */
    if (count === null) {
      /* unknown (Enter beat the debounce) — the /search landing validates */
      try { sessionStorage.setItem('tt-ta-pending', JSON.stringify({ q: q, ts: Date.now() })); } catch (e) {}
      return;
    }
    ga('tt_search', { search_term: q, results: count });
    saveRecents(recInsert(recents(), q, Date.now()));
  }
  /* deferred commit: on the /search results page, settle the pending query
     against the REAL rendered grid (commit if products, drop if empty) */
  function settlePending() {
    /* Doomed pre-redirect landing (see main-search.liquid): the grid is unfiltered —
       leave tt-ta-pending intact so the FILTERED landing settles it truthfully. */
    if (window.__ttSearchRedirecting) return;
    var raw = null;
    try { raw = sessionStorage.getItem('tt-ta-pending'); } catch (e) { return; }
    if (!raw) return;
    try { sessionStorage.removeItem('tt-ta-pending'); } catch (e) {}
    var p = null;
    try { p = JSON.parse(raw); } catch (e) { return; }
    if (!p || !p.q || Date.now() - p.ts > 60000) return;
    if (!document.body || !document.body.classList.contains('template-search')) return;
    var urlQ = '';
    try { urlQ = new URLSearchParams(location.search).get('q') || ''; } catch (e) {}
    if (urlQ.replace(/\s+/g, ' ').trim() !== p.q) return;
    var n = document.querySelectorAll('.grid-product').length;
    if (n > 0) {
      ga('tt_search', { search_term: p.q, results: n });
      saveRecents(recInsert(recents(), p.q, Date.now()));
    } else {
      ga('tt_search_zero', { search_term: p.q, source: 'landing' });
    }
  }
  /* the SAME "אין לנו" answer on the full /search results page (a user who
     pressed Enter on kipling lands here, not in the dropdown) */
  function searchPageZero() {
    if (window.__ttSearchRedirecting) return; /* doomed pre-redirect landing */
    if (!document.body || !document.body.classList.contains('template-search')) return;
    if (document.querySelectorAll('.grid-product').length) return;
    var q = '';
    try { q = (new URLSearchParams(location.search).get('q') || '').replace(/\s+/g, ' ').trim(); } catch (e) { return; }
    if (!q) return;
    var nc = notCarriedHit(q);
    var isStores = !nc && wordHit(q, STORES_WORDS);
    if (!nc && !isStores) return;
    var host = document.querySelector('.search-content') || document.querySelector('#MainContent');
    if (!host || host.querySelector('.tt-nc')) return;
    var box = document.createElement('div');
    box.className = 'tt-nc';
    box.appendChild(zeroStateEl(q, nc, isStores));
    host.insertBefore(box, host.firstChild);
    ga('tt_not_carried', { search_term: q, kind: isStores ? 'stores' : 'brand', source: 'landing' });
  }

  function ga(ev, params) {
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: ev }, params)); } catch (e) {}
  }

  function catRowHtml(q) {
    var words = q.split(/\s+/).filter(Boolean);
    for (var i = 0; i < CATS.length; i++) {
      var hit = CATS[i].k.some(function (key) {
        if (key.indexOf(' ') !== -1) return q.indexOf(key) !== -1;
        return words.some(function (w) {
          if (w === key) return true;
          if (key.length >= 4 && w.indexOf(key) === 0) return true;
          return key.length >= 4 && w.length >= 4 && key.indexOf(w) === 0;
        });
      });
      if (hit) {
        return '<div class="tt-ta__cats">' + CATS[i].links.map(function (l) {
          return '<a class="tt-ta__cat" href="' + l[1] + '">' + esc(l[0]) + ' ›</a>';
        }).join('') + '</div>';
      }
    }
    return '';
  }

  /* focus panel — DOM-built (textContent/dataset cannot break out of
     attributes, unlike the old string concatenation), chips carry dir="auto"
     for Latin/numeric queries + listbox/option roles for the kb-nav contract */
  function chipsRow(qs, labelId, kind) {
    var row = document.createElement('div');
    row.className = 'tt-ta__chips';
    row.setAttribute('role', 'listbox');
    if (labelId) row.setAttribute('aria-labelledby', labelId);
    qs.forEach(function (q) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'tt-ta__chip';
      b.setAttribute('role', 'option');
      b.dir = 'auto';
      b.dataset.q = q;
      if (kind) b.dataset.kind = kind;
      var t = document.createElement('span');
      t.className = 'tt-ta__chip-txt';
      t.textContent = q;
      b.appendChild(t);
      row.appendChild(b);
    });
    return row;
  }
  /* browse fallback under the query chips (SHEIN/Zalando pattern, bench
     2026-07-11): plain collection links, sales-ranked. <a> chips without
     data-q ride the existing kb/click paths and navigate natively. */
  var PANEL_CATS = [
    ['מזוודות', '/collections/מזוודות'],
    ['עלייה למטוס', '/collections/מזוודות-עלייה-למטוס'],
    ['תיקי גב לנשים', '/collections/תיקי-גב-לנשים'],
    ['תיקי גב לגבר', '/collections/תיקי-גב-לגבר'],
    ['ארנקים', '/collections/ארנקי-נשים'],
    ['תיקים למחשב', '/collections/תיקי-גב-למחשב']
  ];
  function catsRowEl(labelId) {
    var row = document.createElement('div');
    row.className = 'tt-ta__chips';
    if (labelId) row.setAttribute('aria-labelledby', labelId);
    PANEL_CATS.forEach(function (c) {
      var a = document.createElement('a');
      a.className = 'tt-ta__chip';
      a.href = c[1];
      a.dir = 'auto';
      var t = document.createElement('span');
      t.className = 'tt-ta__chip-txt';
      t.textContent = c[0];
      a.appendChild(t);
      row.appendChild(a);
    });
    return row;
  }
  function focusPanelEl(instanceN) {
    var wrap = document.createElement('div');
    wrap.className = 'tt-ta tt-ta--panel';
    var r = recents().slice(0, 5);
    var rKeys = {};
    if (r.length) {
      r.forEach(function (e) { rKeys[e.k] = 1; });
      var lab = document.createElement('div');
      lab.className = 'tt-ta__label tt-ta__label--row';
      var labTxt = document.createElement('span');
      labTxt.id = 'tt-lab-recent-' + instanceN;
      labTxt.textContent = 'חיפושים אחרונים';
      var clr = document.createElement('button');
      clr.type = 'button';
      clr.className = 'tt-ta__clear';
      clr.setAttribute('data-tt-clear', '1');
      clr.setAttribute('aria-label', 'ניקוי החיפושים האחרונים');
      clr.textContent = 'נקה';
      lab.appendChild(labTxt);
      lab.appendChild(clr);
      wrap.appendChild(lab);
      wrap.appendChild(chipsRow(r.map(function (e) { return e.q; }), labTxt.id, 'recent'));
    }
    /* a term the user just searched shouldn't appear twice in one panel —
       prefix-aware, so recent "טרול" also hides popular "טרולי" */
    var rKeyList = Object.keys(rKeys);
    var pop = POPULAR.filter(function (p) {
      var pk = normKey(p);
      return !rKeyList.some(function (rk) {
        return rk === pk || (rk.length >= 4 && pk.length >= 4 && (rk.indexOf(pk) === 0 || pk.indexOf(rk) === 0));
      });
    });
    if (pop.length) {
      var lab2 = document.createElement('div');
      lab2.className = 'tt-ta__label';
      lab2.id = 'tt-lab-pop-' + instanceN;
      lab2.textContent = 'חיפושים פופולריים';
      wrap.appendChild(lab2);
      wrap.appendChild(chipsRow(pop, lab2.id, 'popular'));
    }
    var lab3 = document.createElement('div');
    lab3.className = 'tt-ta__label';
    lab3.id = 'tt-lab-cats-' + instanceN;
    lab3.textContent = 'קטגוריות';
    wrap.appendChild(lab3);
    wrap.appendChild(catsRowEl(lab3.id));
    return wrap;
  }

  function highlight(container, q) {
    var words = q.split(/\s+/).filter(function (w) { return w.length >= 2; });
    if (!words.length) return;
    container.querySelectorAll('.tt-ta__title').forEach(function (t) {
      var out = esc(t.textContent);
      words.forEach(function (w) {
        out = out.replace(new RegExp('(' + rxEsc(esc(w)) + ')', 'g'), '<mark>$1</mark>');
      });
      t.innerHTML = out;
    });
  }

  function init() {
    /* neutralize the 417-dead built-in fetch — __ttTypeahead owns fetching.
       GOTCHA: custom.js loads BEFORE theme.js defines the element, so a plain
       customElements.get() here returns undefined and the theme's slow 417
       response would wipe our painted results. whenDefined() patches the
       prototype the moment the class registers; the per-element instance
       shadow below covers every ordering. */
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('predictive-search').then(function () {
        var PS = customElements.get('predictive-search');
        if (PS) PS.prototype.getSearchResults = function () {};
      });
    }

    var instanceSeq = 0;
    document.querySelectorAll('predictive-search').forEach(function (el) {
      var input = el.querySelector('input[type="search"]');
      var results = el.querySelector('#predictive-search');
      var form = el.querySelector('form');
      if (!input || !results || el.__ttTaBound) return;
      el.__ttTaBound = true;
      /* instance shadow: survives custom-element upgrade and beats the
         prototype method regardless of script order */
      el.getSearchResults = function () {};

      var instanceN = ++instanceSeq; /* two instances share the DOM on /search — ids must not collide */
      var timer = null;
      var instTimer = null;
      var ctrl = null;
      var lastQ = '';

      function setExpanded(v) { input.setAttribute('aria-expanded', v ? 'true' : 'false'); }
      function afterShow() {
        results.style.display = 'block';
        results.classList.remove('tt-ta-loading');
        el.classList.add('is-active');
        setExpanded(true);
        var i = 0;
        results.querySelectorAll('.tt-ta__item, .tt-ta__all, .tt-ta__cat, .tt-ta__chip').forEach(function (o) { o.id = 'tt-opt-' + instanceN + '-' + (i++); });
      }
      function show(html) { results.innerHTML = html; afterShow(); }
      function showNode(node) { results.innerHTML = ''; results.appendChild(node); afterShow(); }
      function hide() {
        results.style.display = 'none';
        results.innerHTML = '';
        results.classList.remove('tt-ta-loading');
        lastQ = '';
        setExpanded(false);
        input.removeAttribute('aria-activedescendant');
      }
      function showFocusPanel() { showNode(focusPanelEl(instanceN)); lastQ = ''; }
      /* the results count of whatever .tt-ta is painted right now */
      function paintedCount() {
        var ta = results.querySelector('.tt-ta');
        if (!ta) return null;
        var c = parseInt(ta.getAttribute('data-results-count') || '', 10);
        return isNaN(c) ? null : c;
      }

      function paint(q, sectionHtml) {
        var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        var ta = doc.querySelector('.tt-ta');
        if (!ta || !ta.innerHTML.trim()) { hide(); return; }
        var isZero = ta.getAttribute('data-results-count') === '0';
        /* Ido 2026-07-11: order is all-results link → category row → products */
        var catHtml = catRowHtml(q);
        if (catHtml) {
          var allEl = ta.querySelector('.tt-ta__all');
          if (allEl) allEl.insertAdjacentHTML('afterend', catHtml);
          else ta.insertAdjacentHTML('afterbegin', catHtml);
        }
        show(ta.outerHTML);
        if (!isZero) highlight(results, q);
        else {
          ga('tt_search_zero', { search_term: q });
          /* "אין לנו" — a not-carried brand or a branch-city search gets an
             honest answer + alternatives instead of the generic dead end */
          var nc = notCarriedHit(q);
          var isStores = !nc && wordHit(q, STORES_WORDS);
          if (nc || isStores) {
            var taEl = results.querySelector('.tt-ta');
            if (taEl) {
              taEl.innerHTML = '';
              taEl.appendChild(zeroStateEl(q, nc, isStores));
            }
            ga('tt_not_carried', { search_term: q, kind: isStores ? 'stores' : 'brand' });
          } else if (window.__ttInstant && window.__ttInstant.ready()) {
            /* v3: "האם התכוונת" from the local vocabulary (1-edit neighbor) */
            var rr = window.__ttInstant.query(q);
            if (rr && rr.dym) {
              var noneEl = results.querySelector('.tt-ta__none');
              if (noneEl) noneEl.insertAdjacentHTML('afterend',
                '<div class="tt-ta__chips" style="justify-content:center;padding-top:2px"><button type="button" class="tt-ta__chip" data-q="' + attrEsc(rr.dym) + '">האם התכוונת: ' + esc(rr.dym) + '?</button></div>');
            }
          }
        }
        lastQ = q;
      }

      function render(q) {
        var cached = cacheGet(q);
        if (cached) { paint(q, cached); return; }
        if (ctrl) ctrl.abort();
        ctrl = new AbortController();
        if (results.querySelector('[data-instant]')) {
          /* instant results already on screen — silent upgrade when network lands */
        } else if (results.style.display === 'block' && results.innerHTML) {
          results.classList.add('tt-ta-loading');
        } else {
          show('<div class="tt-ta"><div class="tt-ta__loading">מחפש&hellip;</div></div>');
        }
        var url = '/search?q=' + encodeURIComponent(q) +
          '&type=product&options%5Bprefix%5D=last&section_id=' + SECTION_ID;
        fetch(url, { signal: ctrl.signal, headers: { Accept: 'text/html' } })
          .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
          .then(function (text) {
            if (input.value.trim() !== q) return; /* stale guard */
            cacheSet(q, text);
            paint(q, text);
          })
          .catch(function (err) {
            if (err && err.name === 'AbortError') return;
            /* v3: a failed network fetch must not wipe instant local results */
            if (results.querySelector('[data-instant]')) return;
            hide();
          });
      }

      input.addEventListener('input', function () {
        var q = input.value.trim();
        clearTimeout(timer);
        if (!q.length) { showFocusPanel(); return; }
        if (q.length < MIN_CHARS) { hide(); return; }
        if (q === lastQ) return;
        /* v3: instant local paint (~0ms) while the network fetch is in flight */
        if (window.__ttInstant) {
          clearTimeout(instTimer);
          instTimer = setTimeout(function () {
            if (!window.__ttInstant.ready()) return;
            if (input.value.trim() !== q || lastQ === q) return;
            var r = window.__ttInstant.query(q);
            if (r && r.html) { show(r.html); highlight(results, q); }
          }, 120);
        }
        timer = setTimeout(function () { render(q); }, DEBOUNCE_MS);
      });

      input.addEventListener('focus', function () {
        if (!input.value.trim() && results.style.display !== 'block') showFocusPanel();
      });

      /* keyboard: arrows cycle options, Enter follows the active option;
         plain Enter submits the form natively (the submit listener is the
         single save point — the old save-on-every-Enter here is what wrote
         the typo BEFORE the "האם התכוונת" chip corrected it) */
      input.addEventListener('keydown', function (e) {
        if (e.isComposing || e.keyCode === 229) return; /* mobile IME Go key */
        if (results.style.display !== 'block') return;
        var opts = [].slice.call(results.querySelectorAll('.tt-ta__item, .tt-ta__all, .tt-ta__cat, .tt-ta__chip'));
        if (!opts.length) return;
        var cur = results.querySelector('.is-kbd');
        var idx = opts.indexOf(cur);
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          if (cur) cur.classList.remove('is-kbd');
          idx = e.key === 'ArrowDown' ? (idx + 1) % opts.length : (idx - 1 + opts.length) % opts.length;
          var nxt = opts[idx];
          nxt.classList.add('is-kbd');
          if (nxt.scrollIntoView) nxt.scrollIntoView({ block: 'nearest' });
          input.setAttribute('aria-activedescendant', nxt.id || '');
        } else if (e.key === 'Enter' && cur) {
          if (cur.tagName === 'A' && cur.href) {
            e.preventDefault();
            /* the click validates the query that PAINTED these results */
            commitRecent(lastQ || input.value.trim(), paintedCount());
            ga('tt_suggest_click', { search_term: lastQ || input.value.trim(), item_url: cur.getAttribute('href'), method: 'keyboard' });
            location.href = cur.href;
          } else if (cur.getAttribute && cur.getAttribute('data-q')) {
            e.preventDefault();
            /* chip (incl. "האם התכוונת") — save nothing; the follow-up
               search commits if the user goes through with it */
            input.value = cur.getAttribute('data-q');
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }
      });

      /* clicks inside the dropdown: chips re-run the search; product/category
         links commit the PAINTED query (not the raw input, which may be a
         2-char fragment or newer than what painted), then navigate natively */
      results.addEventListener('click', function (e) {
        var clr = e.target.closest && e.target.closest('[data-tt-clear]');
        if (clr) {
          e.preventDefault();
          clearRecents();
          showFocusPanel();
          input.focus();
          return;
        }
        var chip = e.target.closest && e.target.closest('.tt-ta__chip[data-q]');
        if (chip) {
          e.preventDefault();
          /* tag which panel section converts — feeds the next data bake */
          if (chip.dataset.kind) ga('tt_chip_tap', { chip: chip.dataset.q, kind: chip.dataset.kind });
          input.value = chip.getAttribute('data-q');
          input.focus();
          input.dispatchEvent(new Event('input', { bubbles: true }));
          return;
        }
        var link = e.target.closest && e.target.closest('.tt-ta__item, .tt-ta__all, .tt-ta__cat');
        if (link) {
          commitRecent(lastQ || input.value.trim(), paintedCount());
          ga('tt_suggest_click', { search_term: lastQ || input.value.trim(), item_url: link.getAttribute('href'), method: 'click' });
        }
      });

      /* the ONE plain-submit save point: results-gated commit */
      if (form) form.addEventListener('submit', function () {
        var q = input.value.trim();
        var pc = paintedCount();
        commitRecent(q, lastQ === q && pc !== null ? pc : null);
      });
    });

    settlePending();
    searchPageZero();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
   __ttInstant v3 (Ido 2026-07-10 "תרחיב ותשפר"): client-side instant
   search index layered UNDER the v2 typeahead. Local index of all live
   products (products.json, built lazily on first search focus, cached in
   localStorage 6h) renders results at ~0ms while the authoritative
   section-render fetch is in flight; the network result replaces it when
   it lands (S&D synonyms + canonical ranking), local stays if the network
   fails. Hebrew-aware matching the platform doesn't offer:
   - double-vav/yud collapse: מזוודה≡מזודה, סמסונייט≡סמסוניט
   - geresh stripping: צ'ימידן≡צימידן, ג'נספורט≡גנספורט
   - final-letter normalization, 1-edit typo tolerance (tokens ≥4)
   - brand transliteration shadow-tokens: "אמריקן טוריסטר" hits
     American Tourister products with no synonym round-trip
   - "האם התכוונת" suggestion on zero results (vocab 1-edit neighbor)
   Kill switch: window.__ttInstantOff = true.
   ============================================================ */
(function () {
  if (window.__ttInstantBound || window.__ttInstantOff) return;
  window.__ttInstantBound = true;

  var LS_KEY = 'tt-idx-v3';
  var TTL_MS = 6 * 3600 * 1000;
  var MAX_PAGES = 9;
  var DEBOUNCE_MS = 120; /* faster than the 250ms network debounce */
  var MIN_CHARS = 2;

  /* EN brand/term → Hebrew shadow tokens (from the synonym research;
     normalized forms — vav/yud collapsed, no geresh) */
  var SHADOW = {
    'samsonite': ['סמסוניט'],
    'american': ['אמריקן', 'אמריקאן'],
    'tourister': ['טוריסטר'],
    'victorinox': ['ויקטורינוקס'],
    'wenger': ['ונגר'],
    'delsey': ['דלסי'],
    'eastpak': ['איסטפק'],
    'jansport': ['גנספורט'],
    'herschel': ['הרשל'],
    'fjallraven': ['פילרבן'],
    'kanken': ['קנקן'],
    'tommy': ['טומי'],
    'hilfiger': ['הילפיגר'],
    'under': ['אנדר'],
    'armour': ['ארמור'],
    'armor': ['ארמור'],
    'nautica': ['נאוטיקה'],
    'slazenger': ['סלזנגר', 'שלזינגר'],
    'travelite': ['טרוליט'],
    'roncato': ['רונקטו'],
    'polo': ['פולו'],
    'replay': ['ריפלי'],
    'rains': ['רינס'],
    'dkny': ['דיקני'],
    'camel': ['קמל'],
    'visconti': ['ויסקונטי'],
    'chesterfield': ['צסטרפילד'],
    'verage': ['ורג'],
    'emanuel': ['עמנואל', 'אמנואל'],
    'stories': ['סטוריז'],
    'storeis': ['סטוריז'],
    'ferrini': ['פריני'],
    'echolac': ['אקולק'],
    'aoking': ['אוקינג'],
    'suitcase': ['מזודה'],
    'luggage': ['מזודות'],
    'backpack': ['תיק', 'גב'],
    'גב': ['ילקוט', 'תרמיל'],
    'wallet': ['ארנק'],
    'trolley': ['טרולי'],
    'cabin': ['קבינה'],
    'duffel': ['דאפל'],
    'umbrella': ['מטריה'],
    'bond': ['בונד'],
    'weekend': ['ויקנד'],
    'messenger': ['מסנגר'],
    'tote': ['טוטבג'],
    'passport': ['דרכון'],
    'golden': ['גולדן'],
    'wolf': ['וולף'],
    'rollux': ['רולוקס'],
    'ryden': ['רידן'],
    'rossini': ['רוסיני'],
    'cavalli': ['קואלי']
  };

  function norm(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[׳‘’'"״“”`]/g, '')  /* geresh/quotes out */
      .replace(/ם/g, 'מ').replace(/ן/g, 'נ').replace(/ץ/g, 'צ').replace(/ף/g, 'פ').replace(/ך/g, 'כ')
      .replace(/וו/g, 'ו').replace(/יי/g, 'י');
  }
  function tokens(s) { return norm(s).split(/[^a-z0-9֐-׿]+/).filter(function (t) { return t.length >= 2; }); }

  /* Damerau-Levenshtein distance ≤1 quick check */
  function within1(a, b) {
    if (a === b) return true;
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > 1) return false;
    var i = 0, j = 0, edits = 0;
    while (i < la && j < lb) {
      if (a[i] === b[j]) { i++; j++; continue; }
      if (edits++) return false;
      if (la === lb) {
        /* substitution or transposition */
        if (a[i + 1] === b[j] && a[i] === b[j + 1]) { i += 2; j += 2; }
        else { i++; j++; }
      } else if (la > lb) { i++; } else { j++; }
    }
    return (edits + (la - i) + (lb - j)) <= 1;
  }

  var IDX = null;      /* {base, items:[{t,h,p,c,i,tok:[..]}], vocab:Map} */
  var building = false;

  function buildFrom(products, imgBase) {
    var vocab = Object.create(null);
    var items = products.map(function (p) {
      var tok = tokens(p.t + ' ' + p.v + ' ' + (p.y || ''));
      /* shadow transliteration tokens */
      var extra = [];
      tok.forEach(function (t) { if (SHADOW[t]) extra = extra.concat(SHADOW[t]); });
      tok = tok.concat(extra);
      tok.forEach(function (t) { vocab[t] = (vocab[t] || 0) + 1; });
      return { t: p.t, h: p.h, p: p.p, c: p.c, i: p.i, tok: tok, v: norm(p.v) };
    });
    IDX = { items: items, vocab: vocab, base: imgBase };
  }

  function loadCache() {
    try {
      var raw = localStorage.getItem(LS_KEY);
      if (!raw) return false;
      var j = JSON.parse(raw);
      if (!j.ts || Date.now() - j.ts > TTL_MS) return false;
      buildFrom(j.products, j.base);
      return true;
    } catch (e) { return false; }
  }

  function buildIndex() {
    if (IDX || building) return;
    if (loadCache()) return;
    building = true;
    var all = [];
    var base = null;
    var fetchPage = function (n, attempt) {
      if (n > MAX_PAGES) { finish(); return; }
      fetch('/collections/all/products.json?limit=250&page=' + n)
        .then(function (r) {
          if (!r.ok) {
            /* rate-limited/hiccup: retry the SAME page once after a backoff —
               a silent finish() here truncated the index (AT products lost) */
            if (!attempt) { setTimeout(function () { fetchPage(n, 1); }, 1200); throw { handled: true }; }
            return { products: [] };
          }
          return r.json();
        })
        .then(function (j) {
          var ps = j.products || [];
          ps.forEach(function (x) {
            var avail = x.variants && x.variants.some(function (vv) { return vv.available; });
            if (!avail) return;
            var v0 = x.variants && x.variants[0];
            var img = x.images && x.images[0] ? x.images[0].src : null;
            if (img && !base) base = img.slice(0, img.lastIndexOf('/') + 1);
            all.push({
              t: x.title, h: x.handle, v: x.vendor || '', y: x.product_type || '',
              p: v0 ? v0.price : null, c: v0 ? v0.compare_at_price : null,
              i: img ? (base && img.indexOf(base) === 0 ? img.slice(base.length) : img) : null
            });
          });
          if (ps.length < 250) { finish(); } else { setTimeout(function () { fetchPage(n + 1); }, 350); }
        })
        .catch(function (e) { if (!e || !e.handled) finish(); });
    };
    var finish = function () {
      building = false;
      if (!all.length) return;
      try { localStorage.setItem(LS_KEY, JSON.stringify({ ts: Date.now(), base: base, products: all })); } catch (e) {}
      buildFrom(all, base);
    };
    fetchPage(1);
  }

  function money(v) {
    if (v == null) return '';
    var n = parseFloat(v);
    return (n % 1 ? n.toFixed(2) : n.toFixed(0)) + ' ₪';
  }

  function imgUrl(it) {
    if (!it.i) return null;
    var src = it.i.indexOf('http') === 0 ? it.i : (IDX.base || '') + it.i;
    /* request a small rendition */
    return src.replace(/(\.[a-z]+)(\?|$)/, '_160x$1$2');
  }

  function search(q) {
    if (!IDX) return null;
    var qt = tokens(q);
    if (!qt.length) return { hits: [] };
    var hits = [];
    for (var i = 0; i < IDX.items.length; i++) {
      var it = IDX.items[i];
      var score = 0, ok = true;
      for (var a = 0; a < qt.length; a++) {
        var best = 0, w = qt[a];
        for (var b = 0; b < it.tok.length; b++) {
          var t = it.tok[b];
          var s = 0;
          if (t === w) s = 3;
          else if (t.indexOf(w) === 0) s = 2;
          else if (w.length >= 4 && Math.abs(t.length - w.length) <= 1 && within1(t, w)) s = 1.2;
          else if (w.length >= 3 && t.indexOf(w) > 0) s = 0.8;
          if (s > best) best = s;
          if (best >= 3) break;
        }
        if (!best) { ok = false; break; }
        score += best;
      }
      if (ok) {
        if (it.v && qt.indexOf(it.v) !== -1) score += 0.5;
        hits.push({ it: it, s: score });
      }
    }
    hits.sort(function (x, y) { return y.s - x.s; });
    return { hits: hits.slice(0, 8), total: hits.length };
  }

  function didYouMean(q) {
    if (!IDX) return null;
    var qt = tokens(q);
    for (var i = 0; i < qt.length; i++) {
      var w = qt[i];
      if (w.length < 4) continue;
      var bestTok = null, bestFreq = 0;
      for (var t in IDX.vocab) {
        if (IDX.vocab[t] >= 3 && t !== w && Math.abs(t.length - w.length) <= 1 && within1(t, w) && IDX.vocab[t] > bestFreq) {
          bestFreq = IDX.vocab[t]; bestTok = t;
        }
      }
      if (bestTok) {
        var fixed = qt.slice(); fixed[i] = bestTok;
        return fixed.join(' ');
      }
    }
    return null;
  }

  function esc(s) { var d = document.createElement('div'); d.textContent = s; return d.innerHTML; }

  function renderLocal(q, res) {
    if (!res || !res.hits.length) return null;
    var rows = res.hits.map(function (h) {
      var img = imgUrl(h.it);
      var price = h.it.c && parseFloat(h.it.c) > parseFloat(h.it.p)
        ? '<s class="tt-ta__was">' + money(h.it.c) + '</s> <span class="tt-ta__now tt-ta__now--sale">' + money(h.it.p) + '</span>'
        : '<span class="tt-ta__now">' + money(h.it.p) + '</span>';
      return '<li class="tt-ta__row" role="presentation"><a class="tt-ta__item" role="option" href="/products/' + encodeURIComponent(h.it.h) + '">' +
        (img ? '<img class="tt-ta__img" src="' + img + '" width="56" height="56" loading="lazy" alt="">' : '<span class="tt-ta__img tt-ta__img--empty" aria-hidden="true"></span>') +
        '<span class="tt-ta__info"><span class="tt-ta__title">' + esc(h.it.t) + '</span><span class="tt-ta__price">' + price + '</span></span></a></li>';
    }).join('');
    /* Ido 2026-07-11: all-results link above the products, no count */
    return '<div class="tt-ta" data-instant="1" data-results-count="' + res.total + '">' +
      '<a class="tt-ta__all" href="/search?q=' + encodeURIComponent(q) + '&type=product&filter.v.availability=1">לכל התוצאות ›</a>' +
      '<ul class="tt-ta__list" role="listbox" aria-label="הצעות חיפוש">' + rows + '</ul></div>';
  }

  window.__ttInstant = {
    ensure: buildIndex,
    query: function (q) {
      var res = search(q);
      if (!res) return null;
      if (!res.hits.length) {
        var dym = didYouMean(q);
        return { html: null, dym: dym };
      }
      return { html: renderLocal(q, res), dym: null };
    },
    ready: function () { return !!IDX; }
  };

  /* lazy build triggers: first focus on any search input */
  function arm() {
    document.querySelectorAll('predictive-search input[type="search"]').forEach(function (inp) {
      if (inp.__ttInstArm) return;
      inp.__ttInstArm = true;
      inp.addEventListener('focus', buildIndex, { once: true });
      inp.addEventListener('input', buildIndex, { once: true });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arm);
  else arm();
})();

/* ============================================================
   __oosVariantSwitch (Ido 2026-08-17, approved mockup): a ?variant= link
   whose variant is sold out stranded shoppers on a dead "אזל המלאי"
   button (Google-Shopping feed links, collection-card swatches, shared
   links). If the LANDED value is sold out and another value is available,
   auto-select the first available one and say why in a small notice above
   the buy button. Fully-sold-out products are untouched (the honest אזל
   stays), and plain entries never see this — the server already lands on
   an available variant. Revertible: delete this IIFE.
   ============================================================ */
(function __oosVariantSwitch() {
  if (window.location.search.indexOf('variant=') === -1) return;

  var switchedName = null, switchedValue = null, clickTarget = null;

  function esc(s) { var d = document.createElement('div'); d.textContent = s; return d.innerHTML; }

  function visibleSubmit() {
    var btns = document.querySelectorAll('form[action*="/cart/add"] button[type="submit"], button[name="add"]');
    for (var i = 0; i < btns.length; i++) { if (btns[i].offsetParent) return btns[i]; }
    return null;
  }

  function attempt(tries) {
    /* the variant radios live OUTSIDE the cart form in Impulse — select globally */
    var radios = document.querySelectorAll('input[type="radio"][data-variant-input]');
    if (!radios.length) {
      if (tries < 10) setTimeout(function () { attempt(tries + 1); }, 250);
      return;
    }
    var bad = null;
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].checked && radios[i].classList.contains('disabled')) { bad = radios[i]; break; }
    }
    if (!bad) return;
    var group = document.querySelectorAll('input[type="radio"][data-variant-input][name="' + bad.name.replace(/"/g, '\\"') + '"]');
    var target = null;
    for (var j = 0; j < group.length; j++) {
      if (!group[j].classList.contains('disabled')) { target = group[j]; break; }
    }
    if (!target) return; /* הכל אזל — משאירים "אזל המלאי" כן ואמיתי */
    switchedName = bad.name; switchedValue = bad.value; clickTarget = target;
    target.click();
    setTimeout(verifyAndNote, 600);
  }

  function verifyAndNote() {
    /* אם התמה עוד לא הייתה קשובה כשלחצנו — ניסיון נוסף אחד */
    var btn = visibleSubmit();
    if (btn && btn.disabled && clickTarget && !clickTarget.checked) { clickTarget.click(); }
    setTimeout(showNote, 300);
  }

  function showNote() {
    if (document.getElementById('tt-oos-switch-note')) return;
    var btn = visibleSubmit();
    if (!btn || btn.disabled) return; /* ההחלפה לא תפסה — לא מציגים הודעה שקרית */
    var note = document.createElement('div');
    note.id = 'tt-oos-switch-note';
    note.dir = 'rtl';
    note.setAttribute('role', 'status');
    note.style.cssText = 'background:#fdf6ec;border:1px solid #e8d9bd;border-radius:8px;padding:9px 12px;margin:0 0 10px;font-size:13.5px;line-height:1.5;color:#6b5527;text-align:right;letter-spacing:normal;display:flex;gap:7px;align-items:flex-start;';
    var msg = switchedName === 'צבע'
      ? 'צבע <strong>' + esc(switchedValue) + '</strong> אזל מהמלאי — מוצג צבע זמין'
      : 'האפשרות שנבחרה (<strong>' + esc(switchedValue) + '</strong>) אזלה מהמלאי — מוצגת אפשרות זמינה';
    note.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#b08d3f" stroke-width="2" style="flex:0 0 auto;margin-top:2px" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg><span>' + msg + '</span>';
    btn.parentElement.insertBefore(note, btn);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(function () { attempt(1); }, 150); });
  } else {
    setTimeout(function () { attempt(1); }, 150);
  }
})();
